<template>
  <div class="closing-documents">
    <div class="closing-documents__page-col">
    <div class="cd-sticky-toolbar">
    <!-- ── Шапка ─────────────────────────────────────────────── -->
    <div class="closing-documents__header">
      <div class="closing-documents__title-row">
        <h1 class="closing-documents__title">Закрывающие документы</h1>
      </div>

      <q-tabs
        v-model="activeTab"
        dense align="left"
        class="closing-documents__tabs"
        active-color="primary"
        indicator-color="primary"
        @update:model-value="onTabChange"
      >
        <q-tab name="k2sv" label="КС-2В" />
        <q-tab name="avr"  label="АВР" />
        <q-tab name="m19"  label="М19" />
      </q-tabs>
    </div>

    <!-- ── Фильтры и счётчики: блок над таблицей (вне вертикального скролла списка) ─ -->
    <div class="closing-documents__controls-band">
    <!-- ── Фильтры ────────────────────────────────────────────── -->
    <div class="closing-documents__filters-row">
      <div class="closing-documents__filters">

        <q-select
          v-model="filters.resident_id"
          :options="filteredResidentOpts"
          label="ЖК"
          dense outlined clearable
          use-input hide-selected fill-input input-debounce="200"
          class="cd-filter cd-filter--sm"
          emit-value map-options
          :loading="loadingFilters"
          @filter="filterResidents"
          @input-value="onResidentInputValueUpdate"
          @update:model-value="onResidentChange"
        />

        <q-select
          v-model="filters.project_type_id"
          :options="allProjectTypeOptions"
          label="Тип проекта"
          dense outlined clearable
          class="cd-filter cd-filter--sm"
          emit-value map-options
          :disable="!filters.resident_id || !allProjectTypeOptions.length"
          @update:model-value="onProjectTypeChange"
        />

        <q-select
          v-model="filters.contractor_id"
          :options="filteredContractorOpts"
          label="Контрагент"
          dense outlined clearable
          use-input hide-selected fill-input input-debounce="200"
          class="cd-filter cd-filter--sm"
          emit-value map-options
          :disable="!filters.resident_id || !allContractorOptions.length"
          @filter="filterContractors"
          @update:model-value="onContractorChange"
        />

        <q-select
          v-model="filters.project_entrance_id"
          :options="closingEntranceOptions"
          label="Подъезд и Блок"
          dense outlined clearable
          class="cd-filter cd-filter--entrance"
          emit-value map-options
          option-value="project_entrance_id"
          option-label="entrance_name"
          :disable="!filters.resident_id || !filters.project_type_id"
          :loading="closingEntrancesLoading"
          @update:model-value="onProjectEntranceChange"
        />

        <q-select
          v-model="filters.level_num"
          :options="allLevelOptions"
          label="Этаж"
          dense outlined clearable
          class="cd-filter cd-filter--level"
          emit-value map-options
          :disable="!filters.resident_id || !allLevelOptions.length"
          @update:model-value="loadDocuments"
        />

        <q-select
          v-model="filters.status"
          :options="statusOptions"
          label="Статус"
          dense outlined clearable
          class="cd-filter cd-filter--md"
          emit-value map-options
          @update:model-value="loadDocuments"
        />
      </div>

      <div
        v-if="activeTab === 'k2sv'"
        class="cd-create-btn-row row items-center no-wrap q-gutter-xs"
      >
        <q-btn
          color="primary" label="Создать КС-2В"
          unelevated icon="add" no-caps
          class="cd-create-btn"
          :disable="!filters.resident_id"
          @click="openCreateK2svDialog"
        />
        <q-icon
          v-if="filters.resident_id && !canCreateManualClosingFromTable"
          name="tune"
          color="primary"
          size="22px"
          class="cursor-pointer"
        >
          <q-tooltip max-width="280px">
            Не хватает типа проекта или подъезда и блока в фильтрах — нажмите «Создать КС-2В»: откроется окно параметров (без изменения фильтров страницы).
          </q-tooltip>
        </q-icon>
        <q-icon
          v-else-if="!filters.resident_id"
          name="info_outline"
          color="blue-grey-5"
          size="22px"
          class="cursor-pointer"
        >
          <q-tooltip>Сначала выберите ЖК в фильтрах выше</q-tooltip>
        </q-icon>
      </div>
      <q-btn
        v-else-if="activeTab === 'avr'"
        color="primary" label="Создать АВР"
        unelevated icon="add" no-caps
        class="cd-create-btn"
        @click="openCreateAvrDialog"
      />
      <div
        v-else-if="activeTab === 'm19'"
        class="cd-create-btn-row row items-center no-wrap q-gutter-xs"
      >
        <q-btn
          color="primary" label="Создать М19"
          unelevated icon="add" no-caps
          class="cd-create-btn"
          :disable="!filters.resident_id"
          @click="openCreateM19Dialog"
        />
        <q-icon
          v-if="filters.resident_id && filters.project_type_id && !canOpenM19FromClosingFilters()"
          name="tune"
          color="primary"
          size="22px"
          class="cursor-pointer"
        >
          <q-tooltip max-width="280px">
            Тип проекта выбран, но не удалось определить project_id — дождитесь списка подъездов или откройте таблицу с документами. Либо нажмите «Создать М19» ещё раз после загрузки.
          </q-tooltip>
        </q-icon>
        <q-icon
          v-else-if="!filters.resident_id"
          name="info_outline"
          color="blue-grey-5"
          size="22px"
          class="cursor-pointer"
        >
          <q-tooltip>Сначала выберите ЖК в фильтрах выше</q-tooltip>
        </q-icon>
      </div>
    </div>

    <!-- ── Счётчики (только если фильтр выбран) ──────────────── -->
    <div v-if="hasActiveFilter" class="cd-stats">
      <div class="cd-stat">
        <q-icon name="description" color="blue-6" size="18px" />
        <div class="cd-stat__body">
          <span class="cd-stat__value">{{ stats.total }}</span>
          <span class="cd-stat__label">Всего {{ activeTabLabel }}</span>
        </div>
      </div>
      <div class="cd-stat">
        <q-icon name="warning" color="amber-8" size="18px" />
        <div class="cd-stat__body">
          <span class="cd-stat__value">{{ stats.unsigned }}</span>
          <span class="cd-stat__label">Не подписано</span>
          <q-icon name="info_outline" size="14px" color="blue-grey-5" class="cd-stat__hint-icon cursor-pointer">
            <q-tooltip max-width="280px">
              Нет полной подписи по слотам подписантов (или пустой список подписей — тогда смотрят на признак подписи в шапке документа).
            </q-tooltip>
          </q-icon>
        </div>
      </div>
      <div class="cd-stat">
        <q-icon name="edit_note" color="teal-6" size="18px" />
        <div class="cd-stat__body">
          <span class="cd-stat__value">{{ stats.signed }}</span>
          <span class="cd-stat__label">Подписано</span>
          <q-icon name="info_outline" size="14px" color="blue-grey-5" class="cd-stat__hint-icon cursor-pointer">
            <q-tooltip max-width="300px">
              Все строки с полной подписью по слотам подписантов (в том числе уже отправленные в 1С и те, что ещё без отметки 1С).
            </q-tooltip>
          </q-icon>
        </div>
      </div>
      <div class="cd-stat">
        <q-icon name="cloud_done" color="indigo-6" size="18px" />
        <div class="cd-stat__body">
          <span class="cd-stat__value">{{ stats.sent1c }}</span>
          <span class="cd-stat__label">Отправлено в 1С</span>
          <q-icon name="info_outline" size="14px" color="blue-grey-5" class="cd-stat__hint-icon cursor-pointer">
            <q-tooltip max-width="280px">
              По флагу отправки в данных строки; не требует полной подписи по слотам, поэтому может быть меньше «Подписано».
            </q-tooltip>
          </q-icon>
        </div>
      </div>
    </div>
    </div>
    </div>
    <!-- /cd-sticky-toolbar -->

    <!-- ── Шаг: уточнение ЖК / тип / подъезд перед созданием КС-2В или М19 ─ -->
    <q-dialog v-model="preflightOpen" class="cd-preflight-dialog">
      <q-card class="cd-preflight-card">
        <q-card-section class="row items-center no-wrap q-pb-sm cd-preflight-card__head">
          <q-icon name="playlist_add_check" color="primary" size="22px" class="cd-preflight-card__head-icon" />
          <div class="cd-preflight-card__title">{{ preflightDialogTitle }}</div>
          <q-space />
          <q-btn flat round dense size="sm" icon="close" aria-label="Закрыть" @click="preflightCancel" />
        </q-card-section>
        <q-separator />
        <q-card-section class="cd-preflight-card__body q-gutter-sm">
          <div class="text-caption text-grey-8 cd-preflight-card__hint">
            <template v-if="preflightTarget === 'm19'">
              Выберите тип проекта. Подъезд в этом окне не нужен — project_id подставится из списка подъездов по ЖК и типу. Фильтры страницы не меняются.
            </template>
            <template v-else>
              Тип проекта и подъезд с блоком здесь только для открытия создания документа — основные фильтры страницы не меняются.
              Контрагент и этаж при необходимости уточните в фильтрах над таблицей.
            </template>
          </div>
          <q-field outlined dense readonly label="ЖК" stack-label class="cd-preflight-field">
            <template #control>
              <div class="self-center full-width ellipsis">{{ residentLabelForPreflight }}</div>
            </template>
          </q-field>
          <q-select
            v-model="preflightDraft.project_type_id"
            :options="allProjectTypeOptions"
            label="Тип проекта"
            dense outlined clearable
            emit-value map-options
            class="cd-preflight-field"
            :disable="!filters.resident_id || !allProjectTypeOptions.length"
            :loading="loadingFilters && !allProjectTypeOptions.length"
            @update:model-value="onPreflightProjectTypeChange"
          />
          <q-select
            v-if="preflightTarget !== 'm19'"
            v-model="preflightDraft.project_entrance_id"
            :options="preflightEntranceOptions"
            label="Подъезд и Блок"
            dense outlined clearable
            emit-value map-options
            option-value="project_entrance_id"
            option-label="entrance_name"
            class="cd-preflight-field"
            :disable="!filters.resident_id || !preflightDraft.project_type_id"
            :loading="preflightEntrancesLoading"
          />
          <div
            v-if="!canPreflightConfirm && manualCreateDisabledReasonPreflight"
            class="cd-preflight-card__validation"
          >
            <q-icon name="error_outline" color="primary" size="20px" class="cd-preflight-card__validation-icon" aria-hidden="true" />
            <span class="cd-preflight-card__validation-text">{{ manualCreateDisabledReasonPreflight }}</span>
          </div>
        </q-card-section>
        <q-separator />
        <q-card-actions align="right" class="cd-preflight-card__actions">
          <q-btn flat no-caps label="Отмена" @click="preflightCancel" />
          <q-btn
            color="primary"
            unelevated
            no-caps
            :label="preflightConfirmLabel"
            :disable="!canPreflightConfirm"
            @click="preflightConfirmAndOpenManualModal"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- ── Подсказка (нет фильтра) ───────────────────────────── -->
    <div v-if="!hasActiveFilter" class="cd-empty-hint cd-empty-hint--fill">
      <q-icon name="filter_alt" size="36px" color="grey-4" />
      <span>Выберите хотя бы один фильтр для отображения документов</span>
    </div>

    <div v-if="hasActiveFilter" class="cd-table-body-scroll">
      <div
        ref="cdScrollMain"
        class="closing-documents__table-scroll cd-table-scroll-host"
        @scroll.passive="onCdTableScrollHostScroll($event)"
      >
      <div
        v-show="cdTableScrollBarNeeded"
        ref="cdScrollTop"
        class="cd-table-scroll-rail"
        @scroll.passive="onCdTableScrollSync('top', $event)"
      >
        <div
          class="cd-table-scroll-rail__expander"
          :style="{ width: cdTableScrollContentWidth + 'px' }"
        ></div>
      </div>
      <div class="cd-table-scroll-inner">
    <!-- ══ Таблица КС-2В ══════════════════════════════════════ -->
    <q-table
      v-if="activeTab === 'k2sv'"
      :rows="rows"
      :columns="columnsK2sv"
      row-key="floor_map_document_id"
      flat bordered
      :loading="loading"
      :rows-per-page-options="[25, 50, 100]"
      class="cd-table"
      no-data-label="Нет документов КС-2В"
    >
      <template #body-cell-project="props">
        <q-td :props="props">
          <div class="cd-proj">
            <div class="cd-proj__top row items-center no-wrap q-gutter-x-xs">
              <span class="cd-proj__name cd-proj__name--with-pid">{{ props.row.resident_name || '—' }}</span>
              <code v-if="props.row.project_id != null" class="cd-code cd-code--pid cd-proj__pid">{{ props.row.project_id }}</code>
            </div>
            <span v-if="props.row.project_type_name" class="cd-proj__type">{{ props.row.project_type_name }}</span>
          </div>
        </q-td>
      </template>

      <template #body-cell-loc_block="props">
        <q-td :props="props" class="cd-col-loc">
          <div class="cd-loc-block__main">{{ formatClosingBlockLabel(props.row) }}</div>
          <div v-if="formatClosingEntranceLine(props.row)" class="cd-loc-block__sub text-caption text-grey-7">
            {{ formatClosingEntranceLine(props.row) }}
          </div>
        </q-td>
      </template>
      <template #body-cell-loc_floor="props">
        <q-td :props="props" class="cd-col-loc text-center">{{ formatClosingFloorLabel(props.row) }}</q-td>
      </template>

      <template #body-cell-doc_num="props">
        <q-td :props="props">
          <span class="cd-code">{{ props.row.floor_map_document_id }}</span>
        </q-td>
      </template>

      <template #body-cell-date="props">
        <q-td :props="props">{{ props.row.doc_date || '—' }}</q-td>
      </template>

      <template #body-cell-contractor="props">
        <q-td :props="props" class="cd-td-contractor">
          <div class="cd-proj cd-proj--contractor">
            <span class="cd-proj__name">{{ props.row.contractor_name || '—' }}</span>
            <span v-if="props.row.agreement_num" class="cd-proj__type">№ договора: {{ props.row.agreement_num }}</span>
          </div>
        </q-td>
      </template>

      <template #body-cell-status="props">
        <q-td :props="props">
          <div class="cd-status-stack">
            <q-chip
              :color="documentLifecycleColor(props.row)"
              text-color="white" size="sm" dense
            >
              {{ documentLifecycleLabel(props.row) }}
              <q-tooltip
                v-if="statusSignersTooltipEnabled(props.row)"
                anchor="top middle"
                self="bottom middle"
                :offset="[0, 6]"
                class="cd-status-signers-tt q-pa-sm text-body2"
              >
                <div class="text-weight-medium q-mb-xs">Подписанты</div>
                <div
                  v-for="(line, idx) in statusSignersLines(props.row)"
                  :key="idx"
                  class="cd-status-signers-tt__row"
                >{{ line }}</div>
              </q-tooltip>
            </q-chip>
            <div
              v-if="props.row.prorab_k2sv_checked_fio || props.row.prorab_k2sv_checked_at"
              class="cd-k2sv-prorab-badge q-mt-xs cursor-help"
            >
              <q-icon name="verified" size="12px" color="positive" class="cd-k2sv-prorab-badge__ico" />
              <span class="cd-k2sv-prorab-badge__label">Согласовано</span>
              <q-tooltip
                class="bg-grey-9 text-body2"
                anchor="top middle"
                self="bottom middle"
                :offset="[0, 6]"
              >
                {{ closingK2svProrabAgreeFullText(props.row) }}
              </q-tooltip>
            </div>
          </div>
        </q-td>
      </template>

      <template #body-cell-is_1c="props">
        <q-td :props="props" class="text-center cd-td-1c">
          <div class="cd-1c">
            <q-icon
              :name="props.row.is_sent_bi ? 'check_circle' : 'radio_button_unchecked'"
              :color="props.row.is_sent_bi ? 'green-6' : 'grey-4'"
              size="20px"
            >
              <q-tooltip>{{ props.row.is_sent_bi ? 'Отправлен в 1С' : 'Не отправлен в 1С' }}</q-tooltip>
            </q-icon>
            <div
              v-if="props.row.error && String(props.row.error).trim()"
              class="cd-1c__err"
            >
              <q-btn
                flat round dense
                padding="2px"
                class="cd-1c__err-trigger"
                @click.stop
              >
                <q-icon name="error" color="negative" size="22px" />
                <q-menu
                  anchor="bottom middle"
                  self="top middle"
                  :offset="[0, 6]"
                  content-class="cd-err-menu-panel"
                >
                  <div class="cd-err-menu-panel__text text-body2 text-negative">
                    {{ String(props.row.error).trim() }}
                  </div>
                </q-menu>
              </q-btn>
            </div>
            <div v-if="props.row.avr_code" class="cd-1c__codes text-caption text-grey-8">
              <div class="cd-1c__line cd-1c__line--avr">Код АВР: {{ props.row.avr_code }}</div>
            </div>
            <div
              v-if="props.row.guid"
              class="cd-1c__guid-row text-caption text-grey-8"
              :title="'GUID: ' + props.row.guid"
            >
              <span class="cd-1c__guid-prefix">GUID:</span>
              <span class="cd-1c__guid-mono">{{ props.row.guid }}</span>
            </div>
          </div>
        </q-td>
      </template>

      <template #body-cell-author="props">
        <q-td :props="props" class="text-center">
          <q-icon v-if="props.row.author_name" name="info" color="grey-5" size="18px" class="cursor-pointer">
            <q-tooltip>{{ props.row.author_name }}</q-tooltip>
          </q-icon>
          <span v-else class="text-grey-4">—</span>
        </q-td>
      </template>

      <template #body-cell-download="props">
        <q-td :props="props" class="text-center cd-td-downloads">
          <div class="row no-wrap items-center justify-center q-gutter-xs">
            <q-btn
              v-if="canDownloadClosingDoc(props.row)"
              flat dense round icon="picture_as_pdf"
              color="primary" size="sm"
              @click.stop="downloadClosingDocument(props.row)"
            >
              <q-tooltip>Открыть PDF</q-tooltip>
            </q-btn>
            <q-btn
              v-if="canExportClosingExcelRow(props.row)"
              flat dense round icon="table_chart"
              color="teal-8" size="sm"
              @click.stop="exportClosingDocumentExcel(props.row)"
            >
              <q-tooltip>Скачать Excel</q-tooltip>
            </q-btn>
            <span
              v-if="!canDownloadClosingDoc(props.row) && !canExportClosingExcelRow(props.row)"
              class="text-grey-4"
            >—</span>
          </div>
        </q-td>
      </template>

      <template #body-cell-actions="props">
        <q-td :props="props" class="text-center cd-k2sv-followup">
          <template v-if="props.row.status_code === 'CANCELLED'">
            <span class="text-grey-5">—</span>
          </template>
          <template v-else-if="props.row.followup_pending === false">
            <div class="cd-k2sv-followup__state cd-k2sv-followup__state--done text-caption text-teal-8">
              <q-icon name="check_circle" size="18px" class="cd-k2sv-followup__icon" />
              <span>АВР</span>
              <q-tooltip class="bg-grey-9 text-body2" anchor="top middle" self="bottom middle" :offset="[0, 6]">
                Документы уже созданы по этому КС-2В
              </q-tooltip>
            </div>
          </template>
          <template v-else-if="k2svCanCreateAvr3kc(props.row)">
            <q-btn
              unelevated dense no-caps
              label="Создать АВР"
              color="deep-orange" text-color="white" size="sm"
              class="cd-k2sv-followup__btn"
              @click="openCreateAvrFromRow(props.row)"
            >
              <q-tooltip class="bg-grey-9 text-body2" anchor="top middle" self="bottom middle" :offset="[0, 6]">
                Создать бухгалтерский АВР на основании подписанного КС-2В
              </q-tooltip>
            </q-btn>
          </template>
          <template v-else-if="props.row.followup_pending === true">
            <div class="cd-k2sv-followup__state cd-k2sv-followup__state--wait text-caption text-blue-grey-6">
              <q-icon name="draw" size="18px" class="cd-k2sv-followup__icon" />
              <span>Нужна подпись</span>
              <q-tooltip class="bg-grey-9 text-body2" anchor="top middle" self="bottom middle" :offset="[0, 6]">
                Сначала подпишите КС-2В — затем здесь появится кнопка создания АВР
              </q-tooltip>
            </div>
          </template>
          <span v-else class="text-grey-5">—</span>
        </q-td>
      </template>
    </q-table>

    <!-- ══ Таблица АВР ════════════════════════════════════════ -->
    <q-table
      v-else-if="activeTab === 'avr'"
      :rows="rows"
      :columns="columnsAvr"
      row-key="floor_map_document_id"
      flat bordered
      :loading="loading"
      :rows-per-page-options="[25, 50, 100]"
      class="cd-table"
      no-data-label="Нет документов АВР"
    >
      <template #body-cell-project="props">
        <q-td :props="props">
          <div class="cd-proj">
            <div class="cd-proj__top row items-center no-wrap q-gutter-x-xs">
              <span class="cd-proj__name cd-proj__name--with-pid">{{ props.row.resident_name || '—' }}</span>
              <code v-if="props.row.project_id != null" class="cd-code cd-code--pid cd-proj__pid">{{ props.row.project_id }}</code>
            </div>
            <span v-if="props.row.project_type_name" class="cd-proj__type">{{ props.row.project_type_name }}</span>
          </div>
        </q-td>
      </template>

      <template #body-cell-loc_block="props">
        <q-td :props="props" class="cd-col-loc">
          <div class="cd-loc-block__main">{{ formatClosingBlockLabel(props.row) }}</div>
          <div v-if="formatClosingEntranceLine(props.row)" class="cd-loc-block__sub text-caption text-grey-7">
            {{ formatClosingEntranceLine(props.row) }}
          </div>
        </q-td>
      </template>
      <template #body-cell-loc_floor="props">
        <q-td :props="props" class="cd-col-loc text-center">{{ formatClosingFloorLabel(props.row) }}</q-td>
      </template>

      <template #body-cell-doc_num="props">
        <q-td :props="props">
          <span class="cd-code">{{ props.row.floor_map_document_id }}</span>
        </q-td>
      </template>

      <template #body-cell-k2sv_id="props">
        <q-td :props="props" class="text-center">
          <span v-if="props.row.k2sv_floor_map_document_id != null" class="cd-code">{{ props.row.k2sv_floor_map_document_id }}</span>
          <span v-else class="text-grey-5 text-caption">—</span>
        </q-td>
      </template>

      <template #body-cell-date="props">
        <q-td :props="props">{{ props.row.doc_date || '—' }}</q-td>
      </template>

      <template #body-cell-contractor="props">
        <q-td :props="props" class="cd-td-contractor">
          <div class="cd-proj cd-proj--contractor">
            <span class="cd-proj__name">{{ props.row.contractor_name || '—' }}</span>
            <span v-if="props.row.agreement_num" class="cd-proj__type">№ договора: {{ props.row.agreement_num }}</span>
          </div>
        </q-td>
      </template>

      <template #body-cell-sum="props">
        <q-td :props="props" class="text-right cd-sum">{{ formatSum(props.row.avr_sum) }}</q-td>
      </template>

      <template #body-cell-status="props">
        <q-td :props="props">
          <div class="cd-status-block">
            <q-chip
              :color="documentLifecycleColor(props.row)"
              text-color="white" size="sm" dense
            >
              {{ documentLifecycleLabel(props.row) }}
              <q-tooltip
                v-if="statusSignersTooltipEnabled(props.row)"
                anchor="top middle"
                self="bottom middle"
                :offset="[0, 6]"
                class="cd-status-signers-tt q-pa-sm text-body2"
              >
                <div class="text-weight-medium q-mb-xs">Подписанты</div>
                <div
                  v-for="(line, idx) in statusSignersLines(props.row)"
                  :key="idx"
                  class="cd-status-signers-tt__row"
                >{{ line }}</div>
              </q-tooltip>
            </q-chip>
            <q-chip
              v-if="esfPresent(props.row)"
              color="green-7"
              text-color="white"
              size="sm"
              dense
            >
              ЭСФ
              <q-tooltip>{{ props.row.esf_status }}</q-tooltip>
            </q-chip>
            <q-chip
              v-else
              color="red-5"
              text-color="white"
              size="sm"
              dense
            >
              Нет ЭСФ
            </q-chip>
          </div>
        </q-td>
      </template>

      <template #body-cell-is_1c="props">
        <q-td :props="props" class="text-center cd-td-1c">
          <div class="cd-1c">
            <q-icon
              :name="props.row.is_sent_bi ? 'check_circle' : 'radio_button_unchecked'"
              :color="props.row.is_sent_bi ? 'green-6' : 'grey-4'"
              size="20px"
            >
              <q-tooltip>{{ props.row.is_sent_bi ? 'Отправлен в 1С' : 'Не отправлен в 1С' }}</q-tooltip>
            </q-icon>
            <div
              v-if="props.row.error && String(props.row.error).trim()"
              class="cd-1c__err"
            >
              <q-btn
                flat round dense
                padding="2px"
                class="cd-1c__err-trigger"
                @click.stop
              >
                <q-icon name="error" color="negative" size="22px" />
                <q-menu
                  anchor="bottom middle"
                  self="top middle"
                  :offset="[0, 6]"
                  content-class="cd-err-menu-panel"
                >
                  <div class="cd-err-menu-panel__text text-body2 text-negative">
                    {{ String(props.row.error).trim() }}
                  </div>
                </q-menu>
              </q-btn>
            </div>
            <div v-if="props.row.avr_code" class="cd-1c__codes text-caption text-grey-8">
              <div class="cd-1c__line cd-1c__line--avr">Код АВР: {{ props.row.avr_code }}</div>
            </div>
            <div
              v-if="props.row.guid"
              class="cd-1c__guid-row text-caption text-grey-8"
              :title="'GUID: ' + props.row.guid"
            >
              <span class="cd-1c__guid-prefix">GUID:</span>
              <span class="cd-1c__guid-mono">{{ props.row.guid }}</span>
            </div>
          </div>
        </q-td>
      </template>

      <template #body-cell-kc3_id="props">
        <q-td :props="props" class="cd-td-kc3-id cd-col-3kc-accent">
          <div
            v-if="props.row.kc3_floor_map_document_id != null"
            class="cd-kc3-id-wrap"
            :title="'№ ЗКС: ' + props.row.kc3_floor_map_document_id"
          >
            <span class="cd-code cd-kc3-id__mono">{{ props.row.kc3_floor_map_document_id }}</span>
          </div>
          <span v-else class="text-grey-5">—</span>
        </q-td>
      </template>

      <template #body-cell-kc3_status="props">
        <q-td :props="props" class="cd-td-kc3-status cd-col-3kc-accent">
          <template v-if="props.row.kc3_floor_map_document_id != null">
            <div class="cd-status-block cd-status-block--kc3">
              <q-chip
                :color="documentLifecycleColor(kc3LifecycleRow(props.row))"
                text-color="white" size="sm" dense
              >
                {{ documentLifecycleLabel(kc3LifecycleRow(props.row)) }}
                <q-tooltip
                  v-if="statusSignersTooltipEnabled(kc3LifecycleRow(props.row))"
                  anchor="top middle"
                  self="bottom middle"
                  :offset="[0, 6]"
                  class="cd-status-signers-tt q-pa-sm text-body2"
                >
                  <div class="text-weight-medium q-mb-xs">Подписанты</div>
                  <div
                    v-for="(line, idx) in statusSignersLines(kc3LifecycleRow(props.row))"
                    :key="idx"
                    class="cd-status-signers-tt__row"
                  >{{ line }}</div>
                </q-tooltip>
              </q-chip>
            </div>
          </template>
          <span v-else class="text-grey-5">—</span>
        </q-td>
      </template>

      <template #body-cell-kc3_download="props">
        <q-td :props="props" class="text-center cd-td-kc3-download cd-col-3kc-accent">
          <q-btn
            v-if="props.row.kc3_floor_map_document_id != null"
            flat
            dense
            round
            icon="picture_as_pdf"
            color="primary"
            size="sm"
            @click.stop="downloadClosingKc3Document(props.row)"
          >
            <q-tooltip>Акт 3КС: без подписи — генерация PDF; подписан — как остальные документы</q-tooltip>
          </q-btn>
          <span v-else class="text-grey-5">—</span>
        </q-td>
      </template>

      <template #body-cell-author="props">
        <q-td :props="props" class="text-center">
          <q-icon v-if="props.row.author_name" name="info" color="grey-5" size="18px" class="cursor-pointer">
            <q-tooltip>{{ props.row.author_name }}</q-tooltip>
          </q-icon>
          <span v-else class="text-grey-4">—</span>
        </q-td>
      </template>

      <template #body-cell-download="props">
        <q-td :props="props" class="text-center cd-td-downloads">
          <div class="row no-wrap items-center justify-center q-gutter-xs">
            <q-btn
              v-if="canDownloadClosingDoc(props.row)"
              flat dense round icon="picture_as_pdf"
              color="primary" size="sm"
              @click.stop="downloadClosingDocument(props.row)"
            >
              <q-tooltip>Открыть PDF</q-tooltip>
            </q-btn>
            <q-btn
              v-if="canExportClosingExcelRow(props.row)"
              flat dense round icon="table_chart"
              color="teal-8" size="sm"
              @click.stop="exportClosingDocumentExcel(props.row)"
            >
              <q-tooltip>Скачать Excel</q-tooltip>
            </q-btn>
            <span
              v-if="!canDownloadClosingDoc(props.row) && !canExportClosingExcelRow(props.row)"
              class="text-grey-4"
            >—</span>
          </div>
        </q-td>
      </template>
    </q-table>

    <!-- ══ Таблица М19 ════════════════════════════════════════ -->
    <q-table
      v-else-if="activeTab === 'm19'"
      :rows="rows"
      :columns="columnsM19"
      row-key="floor_map_document_id"
      flat bordered
      :loading="loading"
      :rows-per-page-options="[25, 50, 100]"
      class="cd-table"
      no-data-label="Нет документов М19"
    >
      <template #body-cell-project="props">
        <q-td :props="props">
          <div class="cd-proj">
            <div class="cd-proj__top row items-center no-wrap q-gutter-x-xs">
              <span class="cd-proj__name cd-proj__name--with-pid">{{ props.row.resident_name || '—' }}</span>
              <code v-if="props.row.project_id != null" class="cd-code cd-code--pid cd-proj__pid">{{ props.row.project_id }}</code>
            </div>
            <span v-if="props.row.project_type_name" class="cd-proj__type">{{ props.row.project_type_name }}</span>
          </div>
        </q-td>
      </template>

      <template #body-cell-loc_block="props">
        <q-td :props="props" class="cd-col-loc">
          <div class="cd-loc-block__main">{{ formatClosingBlockLabel(props.row) }}</div>
          <div v-if="formatClosingEntranceLine(props.row)" class="cd-loc-block__sub text-caption text-grey-7">
            {{ formatClosingEntranceLine(props.row) }}
          </div>
        </q-td>
      </template>
      <template #body-cell-loc_floor="props">
        <q-td :props="props" class="cd-col-loc text-center">{{ formatClosingFloorLabel(props.row) }}</q-td>
      </template>

      <template #body-cell-doc_num="props">
        <q-td :props="props">
          <span class="cd-code">{{ props.row.floor_map_document_id }}</span>
        </q-td>
      </template>

      <template #body-cell-date="props">
        <q-td :props="props">{{ props.row.doc_date || '—' }}</q-td>
      </template>

      <template #body-cell-status="props">
        <q-td :props="props">
          <q-chip
            :color="documentLifecycleColor(props.row)"
            text-color="white" size="sm" dense
          >
            {{ documentLifecycleLabel(props.row) }}
            <q-tooltip
              v-if="statusSignersTooltipEnabled(props.row)"
              anchor="top middle"
              self="bottom middle"
              :offset="[0, 6]"
              class="cd-status-signers-tt q-pa-sm text-body2"
            >
              <div class="text-weight-medium q-mb-xs">Подписанты</div>
              <div
                v-for="(line, idx) in statusSignersLines(props.row)"
                :key="idx"
                class="cd-status-signers-tt__row"
              >{{ line }}</div>
            </q-tooltip>
          </q-chip>
        </q-td>
      </template>

      <template #body-cell-is_1c="props">
        <q-td :props="props" class="text-center cd-td-1c">
          <div class="cd-1c">
            <q-icon
              :name="props.row.is_sent_bi ? 'check_circle' : 'radio_button_unchecked'"
              :color="props.row.is_sent_bi ? 'green-6' : 'grey-4'"
              size="20px"
            >
              <q-tooltip>{{ props.row.is_sent_bi ? 'Отправлен в 1С' : 'Не отправлен в 1С' }}</q-tooltip>
            </q-icon>
            <div
              v-if="props.row.error && String(props.row.error).trim()"
              class="cd-1c__err"
            >
              <q-btn
                flat round dense
                padding="2px"
                class="cd-1c__err-trigger"
                @click.stop
              >
                <q-icon name="error" color="negative" size="22px" />
                <q-menu
                  anchor="bottom middle"
                  self="top middle"
                  :offset="[0, 6]"
                  content-class="cd-err-menu-panel"
                >
                  <div class="cd-err-menu-panel__text text-body2 text-negative">
                    {{ String(props.row.error).trim() }}
                  </div>
                </q-menu>
              </q-btn>
            </div>
            <div v-if="props.row.avr_code" class="cd-1c__codes text-caption text-grey-8">
              <div class="cd-1c__line cd-1c__line--avr">Код АВР: {{ props.row.avr_code }}</div>
            </div>
            <div
              v-if="props.row.guid"
              class="cd-1c__guid-row text-caption text-grey-8"
              :title="'GUID: ' + props.row.guid"
            >
              <span class="cd-1c__guid-prefix">GUID:</span>
              <span class="cd-1c__guid-mono">{{ props.row.guid }}</span>
            </div>
          </div>
        </q-td>
      </template>

      <template #body-cell-m19_comment="props">
        <q-td :props="props" class="text-center cd-td-m19-cmt">
          <q-btn
            v-if="m19HasMaterialsRows(props.row)"
            flat dense no-caps
            color="primary"
            label="Подробнее"
            size="sm"
            @click.stop="openM19MaterialsModal(props.row)"
          />
          <span v-else class="text-grey-5">—</span>
        </q-td>
      </template>

      <template #body-cell-author="props">
        <q-td :props="props" class="text-center">
          <q-icon v-if="props.row.author_name" name="info" color="grey-5" size="18px" class="cursor-pointer">
            <q-tooltip>{{ props.row.author_name }}</q-tooltip>
          </q-icon>
          <span v-else class="text-grey-4">—</span>
        </q-td>
      </template>

      <template #body-cell-download="props">
        <q-td :props="props" class="text-center cd-td-downloads">
          <div class="row no-wrap items-center justify-center q-gutter-xs">
            <q-btn
              v-if="canDownloadClosingDoc(props.row)"
              flat dense round icon="picture_as_pdf"
              color="primary" size="sm"
              @click.stop="downloadClosingDocument(props.row)"
            >
              <q-tooltip>Открыть PDF</q-tooltip>
            </q-btn>
            <q-btn
              v-if="canExportClosingExcelRow(props.row)"
              flat dense round icon="table_chart"
              color="teal-8" size="sm"
              @click.stop="exportClosingDocumentExcel(props.row)"
            >
              <q-tooltip>Скачать Excel</q-tooltip>
            </q-btn>
            <span
              v-if="!canDownloadClosingDoc(props.row) && !canExportClosingExcelRow(props.row)"
              class="text-grey-4"
            >—</span>
          </div>
        </q-td>
      </template>
    </q-table>
      </div>
      </div>
    </div>

    </div>
    <!-- /page-col -->

    <!-- ── Диалог: выбрать КС-2В для создания АВР ───────────── -->
    <q-dialog v-model="selectK2svDialogOpen" :maximized="false">
      <q-card class="cd-select-k2sv-card">
        <q-card-section class="row items-center q-pb-none cd-select-k2sv-card__head">
          <div class="text-h6">Выбрать КС-2В для создания АВР</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>
        <q-separator />
        <q-card-section class="q-pa-md cd-select-k2sv-card__body">
          <SelectK2svForAvrModal
            v-if="selectK2svDialogOpen"
            :filters="filters"
            @selected="onK2svSelectedForAvr"
            @close="selectK2svDialogOpen = false"
          />
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- ── Диалог: создать АВР из строки ─────────────────────── -->
    <q-dialog v-model="avrDialogOpen">
      <q-card style="min-width: 500px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Создать АВР</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>
        <q-separator />
        <q-card-section class="q-pa-md">
          <CreateAvrFromK2svModal
            v-if="avrDialogOpen && selectedDocumentForAvr"
            :document="selectedDocumentForAvr"
            @created="onDocumentCreated"
            @close="avrDialogOpen = false"
          />
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- ── М19: материалы и комментарии ─────────────────────── -->
    <q-dialog v-model="m19MaterialsModalOpen" class="cd-m19-cmt-dialog">
      <q-card class="cd-m19-cmt-card">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Материалы и комментарии</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>
        <q-separator />
        <q-card-section class="q-pa-lg cd-m19-cmt-card__body">
          <M19CommentMaterialsModal
            v-if="m19MaterialsModalOpen && m19MaterialsModalRow"
            :document-id="m19MaterialsModalRow.floor_map_document_id"
            :materials="m19MaterialsForRow(m19MaterialsModalRow)"
          />
        </q-card-section>
      </q-card>
    </q-dialog>

  </div>
</template>

<script>
import { mapActions } from "pinia";
import { useAppStore } from "@/services/store/app";
import { MODAL_NAMES, API_URL, FILE_URL } from "@/constants";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import {
  getClosingDocuments,
  getResidents,
  getProjectTypes,
  getCascadeFilters,
  getClosingDocumentEntrances,
  closingDocumentsRowFullySigned,
} from "@/views/ClosingDocuments/services";
import CreateAvrFromK2svModal from "@/views/ClosingDocuments/components/CreateAvrFromK2svModal.vue";
import SelectK2svForAvrModal from "@/views/ClosingDocuments/components/SelectK2svForAvrModal.vue";
import M19CommentMaterialsModal from "@/views/ClosingDocuments/components/M19CommentMaterialsModal.vue";

const TABS = { k2sv: "КС-2В", avr: "АВР", m19: "М19" };
const TAB_TYPE_CODES = { k2sv: "K2SV", avr: "BUH_AVR", m19: "M19_OVER" };

const COL = (name, label, field, align = "left", extra = {}) =>
  ({ name, label, field, align, sortable: true, ...extra });

/** Колонки из SP закрывающих документов: block_name, entrance (подъезд), level_num (этаж). */
const COL_LOC_BLOCK = {
  name: "loc_block",
  label: "Блок",
  field: "block_name",
  align: "left",
  sortable: false,
  classes: "cd-col-loc cd-col-block",
};
const COL_LOC_FLOOR = {
  name: "loc_floor",
  label: "Этаж",
  field: "level_num",
  align: "center",
  sortable: false,
  classes: "cd-col-loc cd-col-floor",
};

export default {
  name: "ClosingDocuments",
  components: { CreateAvrFromK2svModal, SelectK2svForAvrModal, M19CommentMaterialsModal },

  data() {
    return {
      activeTab: "k2sv",
      /** Монотонный счётчик: отбрасываем ответы устаревших запросов списка документов (смена ЖК / таба). */
      documentsFetchGeneration: 0,
      loading: false,
      rows: [],
      stats: { total: 0, unsigned: 0, signed: 0, sent1c: 0, signedNo1c: 0 },

      filters: {
        resident_id:         null,
        project_type_id:     null,
        contractor_id:       null,
        project_entrance_id: null,
        level_num:           null,
        status:              null,
      },

      // Справочники фильтров (каскадные)
      allResidentOptions:    [],
      filteredResidentOpts:  [],
      allProjectTypeOptions: [],
      allContractorOptions:  [],
      filteredContractorOpts:[],
      closingEntranceOptions: [],
      closingEntrancesLoading: false,
      allLevelOptions:       [],
      loadingFilters:        false,

      statusOptions: [
        { label: "Не подписан", value: "CD_UNSIGNED" },
        { label: "Подписан (в 1С)", value: "CD_SIGNED" },
        { label: "Отправлен в 1С", value: "CD_SENT_1C" },
        { label: "Не отправлен в 1С", value: "CD_NOT_SENT_1C" },
        { label: "Есть ЭСФ", value: "CD_HAS_ESF" },
        { label: "Нет ЭСФ", value: "CD_NO_ESF" },
      ],

      selectK2svDialogOpen: false,
      avrDialogOpen: false,
      selectedDocumentForAvr: null,

      m19MaterialsModalOpen: false,
      m19MaterialsModalRow: null,

      /** Диалог «Уточните параметры» перед модалками КС-2В / М19 */
      preflightOpen: false,
      /** @type {'k2sv' | 'm19' | null} */
      preflightTarget: null,
      /** Тип и подъезд только для префлайта — не пишутся в filters. */
      preflightDraft: { project_type_id: null, project_entrance_id: null },
      preflightEntranceOptions: [],
      preflightEntrancesLoading: false,

      /** Верхняя синяя полоса прокрутки, синхрон с внешним overflow (у q-table скролл был внутри .q-table__middle). */
      cdTableScrollContentWidth: 0,
      cdTableScrollBarNeeded: false,
      cdTableScrollSyncing: false,
      cdTableScrollMeasureTimer: null,
      cdTableResizeObserver: null,

      columnsK2sv: [
        COL("project",    "Проект",     "resident_name"),
        COL_LOC_BLOCK,
        COL_LOC_FLOOR,
        COL("doc_num",    "№ КС2В",      "floor_map_document_id", "left", { sortable: true }),
        COL("date",       "Дата",       "doc_date"),
        COL("contractor", "Контрагент", "contractor_name", "left", { classes: "cd-col-contractor" }),
        COL("status",     "Статус",     "status_code",    "left", { classes: "cd-col-status cd-col-status--k2sv" }),
        COL("is_1c",      "1С",         "is_sent_bi",     "center", { sortable: false, classes: "cd-col-1c" }),
        { name: "author",  label: "Автор",   field: "author_name", align: "center", sortable: false },
        { name: "download", label: "Скачать", field: "aws_url", align: "center", sortable: false },
        { name: "actions", label: "АВР", field: "followup_pending", align: "center", sortable: false },
      ],
      columnsAvr: [
        COL("project",    "Проект",     "resident_name"),
        COL_LOC_BLOCK,
        COL_LOC_FLOOR,
        COL("doc_num",    "№ АВР",       "floor_map_document_id", "left", { sortable: true }),
        {
          name: "k2sv_id",
          label: "ID КС-2В",
          field: "k2sv_floor_map_document_id",
          align: "center",
          sortable: true,
          classes: "cd-col-k2sv-link",
        },
        COL("date",       "Дата",       "doc_date"),
        COL("contractor", "Контрагент", "contractor_name", "left", { classes: "cd-col-contractor" }),
        COL("sum",        "Сумма",      "avr_sum",        "right"),
        COL("status",     "Статус",     "status_code",    "left"),
        COL("is_1c",      "1С",         "is_sent_bi",     "center", { sortable: false, classes: "cd-col-1c" }),
        { name: "author",  label: "Автор",   field: "author_name", align: "center", sortable: false },
        { name: "download", label: "Скачать", field: "aws_url", align: "center", sortable: false },
        {
          name: "kc3_id",
          label: "№ ЗКС",
          field: "kc3_floor_map_document_id",
          align: "center",
          sortable: false,
          classes: "cd-col-kc3-id cd-col-3kc-accent",
        },
        {
          name: "kc3_status",
          label: "Статус ЗКС",
          field: "kc3_status_name",
          align: "center",
          sortable: false,
          classes: "cd-col-kc3-status cd-col-3kc-accent",
        },
        {
          name: "kc3_download",
          label: "Скачать 3КС",
          field: "kc3_floor_map_document_id",
          align: "center",
          sortable: false,
          classes: "cd-col-kc3-dl cd-col-3kc-accent",
        },
      ],
      columnsM19: [
        COL("project",  "Проект",     "resident_name"),
        COL_LOC_BLOCK,
        COL_LOC_FLOOR,
        COL("doc_num",  "№ М19",      "floor_map_document_id", "left", { sortable: true }),
        COL("date",     "Дата",       "doc_date"),
        COL("status",   "Статус",     "status_code", "left"),
        COL("is_1c",    "1С",         "is_sent_bi", "center", { sortable: false, classes: "cd-col-1c" }),
        { name: "m19_comment", label: "Комментарий", field: "m19_materials_json", align: "center", sortable: false, classes: "cd-col-m19-cmt" },
        { name: "author",   label: "Автор", field: "author_name", align: "center", sortable: false },
        { name: "download", label: "Скачать", field: "aws_url", align: "center", sortable: false },
      ],
    };
  },

  computed: {
    activeTabLabel() { return TABS[this.activeTab] || ""; },

    hasActiveFilter() {
      return !!this.filters.resident_id;
    },

    /**
     * Проект для «Создать КС-2В» / М19: как в «Документы» — из выбранного подъезда (params.project_id),
     * иначе из первой строки таблицы закрывающих документов.
     */
    closingManualProjectId() {
      return this.selectedEntranceRow?.project_id ?? this.rows[0]?.project_id ?? null;
    },

    /** Строка выбранного подъезда (как в «Документы»: project_entrance_id + entrance_name). */
    selectedEntranceRow() {
      const id = this.filters.project_entrance_id;
      if (id == null) return null;
      return (this.closingEntranceOptions || []).find((e) => e.project_entrance_id === id) || null;
    },

    /** project_id из первой строки списка подъездов (ЖК + тип) — для М19 без выбранного подъезда. */
    firstClosingEntranceOptionProjectId() {
      for (const e of this.closingEntranceOptions || []) {
        if (e?.project_id != null) return e.project_id;
      }
      return null;
    },

    /** Проект для модалки М19: подъезд из фильтра, иначе первый из списка по типу, иначе строка таблицы. */
    m19ModalProjectId() {
      return (
        this.selectedEntranceRow?.project_id ??
        this.firstClosingEntranceOptionProjectId ??
        this.rows[0]?.project_id ??
        null
      );
    },

    /** Сообщение об ошибке, если М19 не открылся с фильтров страницы (без префлайта). */
    m19OpenCreateDisabledReason() {
      if (!this.filters.resident_id) return "Сначала выберите ЖК в фильтрах выше.";
      if (!this.filters.project_type_id) return "Сначала выберите тип проекта в фильтрах выше.";
      return (
        "Не удалось определить project_id для М19: дождитесь загрузки списка подъездов по ЖК и типу проекта или откройте таблицу с документами."
      );
    },

    firstPreflightEntranceProjectId() {
      for (const e of this.preflightEntranceOptions || []) {
        if (e?.project_id != null) return e.project_id;
      }
      return null;
    },

    /** project_id для подтверждения префлайта М19 без обязательного подъезда. */
    preflightM19ModalProjectId() {
      return (
        this.selectedPreflightEntranceRow?.project_id ??
        this.firstPreflightEntranceProjectId ??
        this.rows[0]?.project_id ??
        null
      );
    },

    canCreateManualClosingFromTable() {
      return !!(
        this.filters.resident_id &&
        this.filters.project_type_id &&
        this.filters.project_entrance_id &&
        this.closingManualProjectId
      );
    },

    manualCreateDisabledReason() {
      if (!this.filters.resident_id) return "Выберите ЖК";
      if (!this.filters.project_type_id) return "Выберите тип проекта";
      if (!this.filters.project_entrance_id) return "Выберите подъезд и блок (одна строка в списке, как в «Документы»)";
      if (!this.closingManualProjectId) {
        return "Не удалось определить проект: выберите подъезд и блок в списке или дождитесь строк в таблице.";
      }
      return "";
    },

    residentLabelForPreflight() {
      const o = this.allResidentOptions.find((r) => r.value === this.filters.resident_id);
      return o?.label || "—";
    },

    preflightDialogTitle() {
      if (this.preflightTarget === "m19") return "Параметры для создания М19";
      return "Параметры для создания КС-2В";
    },

    preflightConfirmLabel() {
      if (this.preflightTarget === "m19") return "Открыть создание М19";
      return "Открыть создание КС-2В";
    },

    selectedPreflightEntranceRow() {
      const id = this.preflightDraft.project_entrance_id;
      if (id == null) return null;
      return (this.preflightEntranceOptions || []).find((e) => e.project_entrance_id === id) || null;
    },

    /** project_id для модалки по выбору в префлайте (или первая строка таблицы как запасной вариант). */
    preflightClosingManualProjectId() {
      return this.selectedPreflightEntranceRow?.project_id ?? this.rows[0]?.project_id ?? null;
    },

    canPreflightConfirm() {
      if (!this.filters.resident_id || !this.preflightDraft.project_type_id) return false;
      if (this.preflightTarget === "m19") {
        return !!this.preflightM19ModalProjectId;
      }
      return !!(
        this.preflightDraft.project_entrance_id &&
        this.preflightClosingManualProjectId
      );
    },

    manualCreateDisabledReasonPreflight() {
      if (!this.filters.resident_id) return "Выберите ЖК в фильтрах страницы";
      if (!this.preflightDraft.project_type_id) return "Выберите тип проекта";
      if (this.preflightTarget === "m19") {
        if (!this.preflightM19ModalProjectId) {
          return "Не удалось определить проект: выберите тип проекта и дождитесь списка подъездов.";
        }
        return "";
      }
      if (!this.preflightDraft.project_entrance_id) return "Выберите подъезд и блок";
      if (!this.preflightClosingManualProjectId) {
        return "Не удалось определить проект: выберите подъезд в списке или дождитесь строк в таблице.";
      }
      return "";
    },
  },

  watch: {
    hasActiveFilter(v) {
      if (v) {
        this.$nextTick(() => {
          this.setupCdTableResizeObserver();
          this.scheduleCdTableScrollMeasure();
        });
      } else {
        this.teardownCdTableResizeObserver();
        this.cdTableScrollContentWidth = 0;
        this.cdTableScrollBarNeeded = false;
      }
    },
    rows: {
      handler() {
        this.scheduleCdTableScrollMeasure();
      },
      deep: true,
    },
    loading(v) {
      if (!v) {
        this.$nextTick(() => {
          this.setupCdTableResizeObserver();
          this.scheduleCdTableScrollMeasure();
        });
      }
    },
    activeTab() {
      this.$nextTick(() => {
        this.setupCdTableResizeObserver();
        this.scheduleCdTableScrollMeasure();
      });
    },
  },

  async mounted() {
    await this.loadDictionaries();
    if (typeof window !== "undefined") {
      window.addEventListener("resize", this.scheduleCdTableScrollMeasure, { passive: true });
    }
    this.$nextTick(() => {
      if (this.hasActiveFilter) {
        this.setupCdTableResizeObserver();
        this.scheduleCdTableScrollMeasure();
      }
    });
  },

  beforeDestroy() {
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.scheduleCdTableScrollMeasure);
    }
    clearTimeout(this.cdTableScrollMeasureTimer);
    this.teardownCdTableResizeObserver();
  },

  methods: {
    ...mapActions(useAppStore, ["setClosingDocumentsCount", "showModal"]),

    async loadDictionaries() {
      // Только ЖК загружаем статично при монтировании (порядок — с бэка, по алфавиту)
      const res = await getResidents();
      const items = res?.data || [];
      this.allResidentOptions = items.map((r) => ({
        label: r.resident_name,
        value: r.resident_id,
      }));
      this.filteredResidentOpts = this.allResidentOptions;
    },

    /**
     * Ключ для группировки одинаковых подписей (несколько project_id на ЖК+тип).
     * @param {object} item
     */
    closingEntranceKey(item) {
      return `${String(item?.entrance ?? "")}|${String(item?.block_name ?? "").trim()}`;
    },

    /**
     * Убирает повторы по project_entrance_id; при одинаковых «Подъезд N, Блок №…»
     * добавляет « — название проекта» (как в отчётах при multi-project).
     */
    normalizeClosingEntranceOptions(list) {
      const raw = Array.isArray(list) ? list : [];
      const seenPe = new Set();
      const deduped = [];
      for (const it of raw) {
        const id = it?.project_entrance_id;
        if (id != null) {
          if (seenPe.has(id)) continue;
          seenPe.add(id);
        }
        deduped.push(it);
      }
      const freq = new Map();
      for (const it of deduped) {
        const k = this.closingEntranceKey(it);
        freq.set(k, (freq.get(k) || 0) + 1);
      }
      return deduped.map((it) => {
        const base =
          it.entrance_name ||
          `Подъезд ${it.entrance}${it.block_name ? `, Блок №${it.block_name}` : ""}`;
        const collision = (freq.get(this.closingEntranceKey(it)) || 0) > 1;
        let entrance_name = base;
        if (collision) {
          const pn = it.project_name != null ? String(it.project_name).trim() : "";
          const suffix = pn || (it.project_id != null ? `проект ${it.project_id}` : "");
          entrance_name = suffix ? `${base} — ${suffix}` : base;
        }
        return { ...it, entrance_name };
      });
    },

    async loadClosingEntrances() {
      this.closingEntranceOptions = [];
      if (!this.filters.resident_id || !this.filters.project_type_id) return;
      this.closingEntrancesLoading = true;
      try {
        const list = await getClosingDocumentEntrances({
          resident_id: this.filters.resident_id,
          project_type_id: this.filters.project_type_id,
        });
        this.closingEntranceOptions = this.normalizeClosingEntranceOptions(list);
        if (this.filters.project_entrance_id != null) {
          const ok = this.closingEntranceOptions.some(
            (e) => e.project_entrance_id === this.filters.project_entrance_id,
          );
          if (!ok) this.filters.project_entrance_id = null;
        }
      } finally {
        this.closingEntrancesLoading = false;
      }
    },

    async loadPreflightEntrances() {
      this.preflightEntranceOptions = [];
      if (!this.filters.resident_id || !this.preflightDraft.project_type_id) return;
      this.preflightEntrancesLoading = true;
      try {
        const list = await getClosingDocumentEntrances({
          resident_id: this.filters.resident_id,
          project_type_id: this.preflightDraft.project_type_id,
        });
        this.preflightEntranceOptions = this.normalizeClosingEntranceOptions(list);
        if (this.preflightDraft.project_entrance_id != null) {
          const ok = this.preflightEntranceOptions.some(
            (e) => e.project_entrance_id === this.preflightDraft.project_entrance_id,
          );
          if (!ok) this.preflightDraft.project_entrance_id = null;
        }
      } finally {
        this.preflightEntrancesLoading = false;
      }
    },

    async onPreflightProjectTypeChange() {
      this.preflightDraft.project_entrance_id = null;
      if (this.filters.resident_id && this.preflightDraft.project_type_id) {
        await this.loadPreflightEntrances();
      }
    },

    resetPreflightDraft() {
      this.preflightDraft = { project_type_id: null, project_entrance_id: null };
      this.preflightEntranceOptions = [];
      this.preflightEntrancesLoading = false;
    },

    /** block_name + entrance для closing_documents__read и closing_documents__cascade__read. */
    closingFilterLocationParams() {
      const row = this.selectedEntranceRow;
      const o = {};
      if (row?.block_name != null && String(row.block_name).trim() !== "") {
        o.block_name = row.block_name;
      }
      if (row?.entrance != null && String(row.entrance).trim() !== "") {
        const n = Number(row.entrance);
        if (!Number.isNaN(n)) o.entrance = n;
      }
      return o;
    },

    // Единый метод пересчёта каскадных опций и перезагрузки документов.
    // resetBelow: массив имён фильтров которые нужно сбросить перед запросом.
    async refreshCascade(resetBelow = []) {
      resetBelow.forEach((k) => { this.filters[k] = null; });

      if (!this.filters.resident_id) {
        this.documentsFetchGeneration += 1;
        this.loading = false;
        this.allProjectTypeOptions  = [];
        this.allContractorOptions   = [];
        this.filteredContractorOpts = [];
        this.closingEntranceOptions = [];
        this.allLevelOptions         = [];
        this.rows                   = [];
        this.recalcStats();
        this.setClosingDocumentsCount(null);
        return;
      }

      this.loadingFilters = true;
      try {
        const params = { resident_id: this.filters.resident_id };
        if (this.filters.project_type_id) params.project_type_id = this.filters.project_type_id;
        if (this.filters.contractor_id)   params.contractor_id   = this.filters.contractor_id;
        Object.assign(params, this.closingFilterLocationParams());

        const res = await getCascadeFilters(params);
        if (!res) return;

        this.allContractorOptions   = (res.contractors || []).map((c) => ({
          label: c.contractor_name,
          value: c.contractor_id,
        }));
        this.filteredContractorOpts = this.allContractorOptions;

        this.allLevelOptions    = (res.levels    || []).map((l) => ({ label: String(l), value: l }));
      } finally {
        this.loadingFilters = false;
      }

      this.loadDocuments();
    },

    // Выбор ЖК — сбрасываем ВСЕ нижние фильтры
    async onResidentChange(val) {
      // Типы проекта грузим отдельно через существующий SP (он фильтрует по правам)
      this.allProjectTypeOptions = [];
      if (val) {
        this.loadingFilters = true;
        try {
          const typesRes = await getProjectTypes(val);
          this.allProjectTypeOptions = (typesRes?.data || []).map((t) => ({
            label: t.project_type_name,
            value: t.project_type_id,
          }));
        } catch { /* silent */ } finally {
          this.loadingFilters = false;
        }
      }
      await this.refreshCascade([
        "project_type_id",
        "contractor_id",
        "project_entrance_id",
        "level_num",
        "status",
      ]);
      if (this.filters.resident_id && this.filters.project_type_id) {
        await this.loadClosingEntrances();
      }
    },

    // Выбор Тип проекта — сбрасываем контрагента, подъезд, этаж; список подъездов как в «Документы»
    async onProjectTypeChange() {
      await this.refreshCascade(['contractor_id', 'project_entrance_id', 'level_num']);
      await this.loadClosingEntrances();
    },

    // Выбор Контрагента — пересчитываем этажи под выбранного подрядчика и локацию
    async onContractorChange() {
      await this.refreshCascade(['level_num']);
    },

    async onProjectEntranceChange() {
      await this.refreshCascade(['level_num']);
    },

    onTabChange() {
      this.documentsFetchGeneration += 1;
      this.rows = [];
      this.recalcStats();
      if (this.filters.resident_id) {
        this.loadDocuments();
      } else {
        this.loading = false;
        this.setClosingDocumentsCount(null);
      }
    },

    resetFilters() {
      this.documentsFetchGeneration += 1;
      this.loading = false;
      Object.keys(this.filters).forEach((k) => { this.filters[k] = null; });
      this.allProjectTypeOptions  = [];
      this.allContractorOptions   = [];
      this.filteredContractorOpts = [];
      this.closingEntranceOptions = [];
      this.allLevelOptions        = [];
      this.rows = [];
      this.recalcStats();
      this.setClosingDocumentsCount(null);
    },

    async onResidentInputValueUpdate(val) {
      const empty = val === "" || val == null;
      if (!empty) return;
      if (this.filters.resident_id == null) return;
      this.filters.resident_id = null;
      await this.onResidentChange(null);
    },

    async loadDocuments() {
      // Не загружаем если ЖК не выбран
      if (!this.filters.resident_id) {
        this.documentsFetchGeneration += 1;
        this.rows = [];
        this.recalcStats();
        this.loading = false;
        this.setClosingDocumentsCount(null);
        this.scheduleCdTableScrollMeasure();
        return;
      }
      const myGeneration = ++this.documentsFetchGeneration;
      this.rows = [];
      this.recalcStats();
      this.loading = true;
      try {
        const params = { doc_type: TAB_TYPE_CODES[this.activeTab] };
        if (this.filters.resident_id)     params.resident_id     = this.filters.resident_id;
        if (this.filters.project_type_id) params.project_type_id = this.filters.project_type_id;
        if (this.filters.contractor_id)   params.contractor_id   = this.filters.contractor_id;
        Object.assign(params, this.closingFilterLocationParams());
        if (this.filters.level_num)       params.level_num       = this.filters.level_num;
        if (this.filters.status)          params.status          = this.filters.status;

        const res = await getClosingDocuments(params);
        if (myGeneration !== this.documentsFetchGeneration) return;
        this.rows = res?.data || [];
        this.recalcStats();
        this.setClosingDocumentsCount(this.stats.signedNo1c || null);
      } catch {
        if (myGeneration !== this.documentsFetchGeneration) return;
        this.rows = [];
        this.recalcStats();
        this.setClosingDocumentsCount(null);
      } finally {
        if (myGeneration === this.documentsFetchGeneration) {
          this.loading = false;
        }
        this.scheduleCdTableScrollMeasure();
      }
    },

    recalcStats() {
      const r = this.rows;
      const signed = (x) => x && closingDocumentsRowFullySigned(x);
      const sent1cFn = (x) => x && (x.is_sent_bi === true || x.is_sent_bi === 1);
      /** Отображение: «Подписано» = любая полная подпись; «Отправлено» = только флаг 1С. signedNo1c — для счётчика в шапке. */
      this.stats = {
        total: r.length,
        unsigned: r.filter((x) => !signed(x)).length,
        signed: r.filter((x) => signed(x)).length,
        sent1c: r.filter((x) => sent1cFn(x)).length,
        signedNo1c: r.filter((x) => signed(x) && !sent1cFn(x)).length,
      };
    },


    filterResidents(val, update) {
      const needle = (val || "").toLowerCase();
      update(() => {
        this.filteredResidentOpts = needle
          ? this.allResidentOptions.filter((o) => o.label?.toLowerCase().includes(needle))
          : this.allResidentOptions;
      });
    },

    filterContractors(val, update) {
      const needle = (val || "").toLowerCase();
      update(() => {
        this.filteredContractorOpts = needle
          ? this.allContractorOptions.filter((o) => o.label?.toLowerCase().includes(needle))
          : this.allContractorOptions;
      });
    },

    /**
     * Тот же смысл, что `this.params` у вкладки «Документы» (ResidentialSettings → EntranceDocuments):
     * контекст ЖК / тип / подъезд / проект для модалок, даже если сами модалки пока не читают все поля.
     */
    resolveProjectTypeName(projectTypeId) {
      const fromRow = this.selectedEntranceRow?.project_type_name;
      if (fromRow) return String(fromRow).trim();
      const id = projectTypeId ?? this.filters.project_type_id;
      const o = (this.allProjectTypeOptions || []).find((t) => t.value === id);
      return (o?.label && String(o.label).trim()) || "";
    },

    modalParamsResidentialShape() {
      const row = this.selectedEntranceRow;
      const pid = this.closingManualProjectId;
      const out = {
        resident_id: this.filters.resident_id,
        project_type_id: this.filters.project_type_id,
        project_entrance_id: this.filters.project_entrance_id,
        project_id: pid,
        project_type_name: this.resolveProjectTypeName(this.filters.project_type_id),
      };
      if (this.filters.contractor_id != null) {
        out.contractor_id = this.filters.contractor_id;
      }
      if (row?.sbs_project_id != null) {
        out.sbs_project_id = row.sbs_project_id;
      }
      return out;
    },

    /**
     * Как `this.filters` внутри EntranceDocuments.vue: этаж таблицы документов и тип документа.
     * У закрывающих «этаж» страницы маппим в `floor` строкой (как в списке этажей документов).
     */
    modalFiltersDocumentsTabShape() {
      return {
        floor:
          this.filters.level_num != null && this.filters.level_num !== ""
            ? String(this.filters.level_num)
            : "",
        floor_map_document_type_id: "",
      };
    },

    /** Контекст для модалки после префлайта — тип и подъезд из черновика, не из filters. */
    modalParamsFromPreflightDraft() {
      const row = this.selectedPreflightEntranceRow;
      const pid = this.preflightClosingManualProjectId;
      const tid = this.preflightDraft.project_type_id;
      const fromRow = this.selectedPreflightEntranceRow?.project_type_name;
      const typeName =
        (fromRow && String(fromRow).trim()) ||
        (this.allProjectTypeOptions || []).find((t) => t.value === tid)?.label ||
        "";
      const out = {
        resident_id: this.filters.resident_id,
        project_type_id: tid,
        project_entrance_id: this.preflightDraft.project_entrance_id,
        project_id: pid,
        project_type_name: typeName ? String(typeName).trim() : "",
      };
      if (this.filters.contractor_id != null) {
        out.contractor_id = this.filters.contractor_id;
      }
      if (row?.sbs_project_id != null) {
        out.sbs_project_id = row.sbs_project_id;
      }
      return out;
    },

    /** Та же модалка, что «Создать КС2В» в Документы (ResidentialSettings): ManualAvrCalls. */
    async ensureProjectTypesForPreflight() {
      if (!this.filters.resident_id || this.allProjectTypeOptions.length) return;
      this.loadingFilters = true;
      try {
        const typesRes = await getProjectTypes(this.filters.resident_id);
        this.allProjectTypeOptions = (typesRes?.data || []).map((t) => ({
          label: t.project_type_name,
          value: t.project_type_id,
        }));
      } catch {
        /* silent */
      } finally {
        this.loadingFilters = false;
      }
    },

    async openPreflightManualCreate(target) {
      this.preflightTarget = target;
      await this.ensureProjectTypesForPreflight();
      const tid = this.filters.project_type_id;
      this.preflightDraft = {
        project_type_id: tid,
        project_entrance_id:
          target === "m19" ? null : tid ? this.filters.project_entrance_id : null,
      };
      if (this.filters.resident_id && this.preflightDraft.project_type_id) {
        await this.loadPreflightEntrances();
      } else {
        this.preflightEntranceOptions = [];
      }
      this.preflightOpen = true;
    },

    preflightCancel() {
      this.preflightOpen = false;
      this.preflightTarget = null;
      this.resetPreflightDraft();
    },

    preflightConfirmAndOpenManualModal() {
      if (!this.canPreflightConfirm) {
        this.showErrorSnackbar(this.manualCreateDisabledReasonPreflight);
        return;
      }
      const mode = this.preflightTarget;
      const options = {
        fromPreflight: true,
        project_id:
          mode === "m19" ? this.preflightM19ModalProjectId : this.preflightClosingManualProjectId,
        params: this.modalParamsFromPreflightDraft(),
        filters: this.modalFiltersDocumentsTabShape(),
      };
      this.preflightOpen = false;
      this.preflightTarget = null;
      this.resetPreflightDraft();
      if (mode === "m19") {
        this.openCreateM19ModalOnly(options);
      } else {
        this.openCreateK2svModalOnly(options);
      }
    },

    openCreateK2svDialog() {
      if (!this.filters.resident_id) {
        this.showErrorSnackbar("Сначала выберите ЖК в фильтрах выше.");
        return;
      }
      if (this.canCreateManualClosingFromTable) {
        this.openCreateK2svModalOnly();
        return;
      }
      void this.openPreflightManualCreate("k2sv");
    },

    openCreateK2svModalOnly(options = null) {
      const fromPreflight = options && options.fromPreflight === true;
      const projectId = fromPreflight ? options.project_id : this.closingManualProjectId;
      if (projectId == null) {
        this.showErrorSnackbar(
          fromPreflight ? this.manualCreateDisabledReasonPreflight : this.manualCreateDisabledReason,
        );
        return;
      }
      const params = fromPreflight ? options.params : this.modalParamsResidentialShape();
      const filters = fromPreflight ? options.filters : this.modalFiltersDocumentsTabShape();
      this.showModal(MODAL_NAMES.ManualAvrCalls, {
        title: "Создать КС2В",
        project_id: projectId,
        params,
        filters,
        onSuccess: () => this.loadDocuments(),
      });
    },

    openCreateAvrDialog() {
      this.selectK2svDialogOpen = true;
    },

    onK2svSelectedForAvr(row) {
      this.selectK2svDialogOpen = false;
      this.selectedDocumentForAvr = row;
      this.avrDialogOpen = true;
    },

    openCreateAvrFromRow(row) {
      if (!this.k2svCanCreateAvr3kc(row)) return;
      this.selectedDocumentForAvr = row;
      this.avrDialogOpen = true;
    },

    /** Нормализация m19_materials_json с бэка (массив объектов с material_name, fact_qty, comments). */
    m19MaterialsForRow(row) {
      const raw = row && row.m19_materials_json;
      if (raw == null) return [];
      if (Array.isArray(raw)) return raw;
      if (typeof raw === "string") {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) return parsed;
          if (parsed && typeof parsed === "object") return [parsed];
        } catch {
          return [];
        }
        return [];
      }
      if (typeof raw === "object") return [raw];
      return [];
    },

    m19HasMaterialsRows(row) {
      return this.m19MaterialsForRow(row).length > 0;
    },

    openM19MaterialsModal(row) {
      this.m19MaterialsModalRow = row;
      this.m19MaterialsModalOpen = true;
    },

    rowIsSigned(row) {
      return closingDocumentsRowFullySigned(row);
    },

    /** Поля связанного ЗКС в том же виде, что у строки АВР — для чипов «Статус» / ЭСФ / подписанты. */
    kc3LifecycleRow(row) {
      if (!row || row.kc3_floor_map_document_id == null) return null;
      return {
        status_code: row.kc3_status_code,
        status_name: row.kc3_status_name,
        have_signed: row.kc3_have_signed,
        assign_signs: Array.isArray(row.kc3_assign_signs) ? row.kc3_assign_signs : [],
      };
    },

    /** КС-2В подписан, по связке ещё нет созданного АВР (логика followup_pending с бэка). */
    k2svCanCreateAvr3kc(row) {
      return !!(row && row.status_code !== "CANCELLED" && row.followup_pending === true && this.rowIsSigned(row));
    },

    /** ЖК + тип проекта + известный project_id — можно открыть М19 без префлайта и без подъезда в фильтре. */
    canOpenM19FromClosingFilters() {
      return !!(
        this.filters.resident_id &&
        this.filters.project_type_id &&
        this.m19ModalProjectId != null
      );
    },

    openCreateM19Dialog() {
      if (!this.filters.resident_id) {
        this.showErrorSnackbar("Сначала выберите ЖК в фильтрах выше.");
        return;
      }
      if (!this.filters.project_type_id) {
        void this.openPreflightManualCreate("m19");
        return;
      }
      if (this.canOpenM19FromClosingFilters()) {
        this.openCreateM19ModalOnly();
        return;
      }
      void this.openPreflightManualCreate("m19");
    },

    openCreateM19ModalOnly(options = null) {
      const fromPreflight = options && options.fromPreflight === true;
      const projectId = fromPreflight ? options.project_id : this.m19ModalProjectId;
      if (projectId == null) {
        this.showErrorSnackbar(
          fromPreflight ? this.manualCreateDisabledReasonPreflight : this.m19OpenCreateDisabledReason,
        );
        return;
      }
      const params = fromPreflight ? options.params : this.modalParamsResidentialShape();
      const filters = fromPreflight ? options.filters : this.modalFiltersDocumentsTabShape();
      this.showModal(MODAL_NAMES.M19Overuse, {
        title: "Создать М19",
        project_id: projectId,
        project_type_name: params?.project_type_name || "",
        params,
        filters,
        wide: true,
        onSuccess: () => this.loadDocuments(),
      });
    },

    onDocumentCreated() { this.loadDocuments(); },

    /** Статус в одну строку: подписан / не подписан (отмена — из БД). Без «Создан» и без дубля про 1С — это колонка «1С». */
    documentLifecycleLabel(row) {
      if (!row) return "—";
      if (row.status_code === "CANCELLED") return row.status_name || "Отменён";
      if (closingDocumentsRowFullySigned(row)) return "Подписан";
      return "Не подписан";
    },

    documentLifecycleColor(row) {
      if (!row) return "grey-5";
      if (row.status_code === "CANCELLED") return "red-5";
      if (closingDocumentsRowFullySigned(row)) return "teal-6";
      return "blue-grey-6";
    },

    /** Полный текст согласования прораба для тултипа (рамка «Согласовано» в ячейке). */
    closingK2svProrabAgreeFullText(row) {
      if (!row) return "";
      let s = "Согласовано прорабом";
      const fio = row.prorab_k2sv_checked_fio != null ? String(row.prorab_k2sv_checked_fio).trim() : "";
      const at = row.prorab_k2sv_checked_at != null ? String(row.prorab_k2sv_checked_at).trim() : "";
      if (fio) s += `: ${fio}`;
      if (at) s += fio ? `, ${at}` : `: ${at}`;
      return s;
    },

    /** Тултип подписантов: только для неотменённых документов. */
    statusSignersTooltipEnabled(row) {
      return row && row.status_code !== "CANCELLED";
    },

    statusSignersLines(row) {
      const raw = row?.assign_signs;
      if (!raw || !Array.isArray(raw) || raw.length === 0) {
        return ["Нет данных о подписантах"];
      }
      return raw.map((it) => {
        const role = it.assign_type_name || "—";
        const fio = it.employee_fio || "—";
        const ok = it.is_signed === true || it.is_signed === 1;
        const d = it.sign_date ? String(it.sign_date).trim() : "";
        if (ok) {
          return d ? `${role}: ${fio} — подписал (${d})` : `${role}: ${fio} — подписал`;
        }
        return `${role}: ${fio} — не подписал`;
      });
    },

    /** Есть ли ЭСФ по строке (непустой статус из БД). */
    esfPresent(row) {
      if (!row) return false;
      return String(row.esf_status || "").trim().length > 0;
    },

    /** Абсолютный URL файла: относительные пути клеим к FILE_URL / API (иначе браузер ведёт на порт фронта). */
    resolveDocumentHref(raw) {
      if (raw == null || raw === "") return "";
      const u = String(raw).trim();
      if (!u) return "";
      if (/^https?:\/\//i.test(u)) return u;
      const base = (FILE_URL || process.env.VUE_APP_DOCUMENT_BASE_URL || API_URL || "").replace(/\/$/, "");
      if (!base) return u;
      const path = u.startsWith("/") ? u : `/${u}`;
      return `${base}${path}`;
    },

    /** Контекст проекта для тех же query/body, что и в Documents.vue (подъезд). */
    downloadClosingDocParams(row) {
      const p = {};
      if (row.project_id != null) p.project_id = row.project_id;
      if (row.project_entrance_id != null) p.project_entrance_id = row.project_entrance_id;
      if (row.project_type_id != null) p.project_type_id = row.project_type_id;
      if (row.resident_id != null) p.resident_id = row.resident_id;
      return p;
    },

    canDownloadClosingDoc(row) {
      if (!row) return false;
      if (closingDocumentsRowFullySigned(row)) return true;
      if (row.master_url && String(row.master_url).trim()) return true;
      if (row.aws_url || row.doc_url) return true;
      return false;
    },

    /** Excel по той же логике данных, что превью/API (достаточно id документа). */
    canExportClosingExcelRow(row) {
      return !!(row && row.floor_map_document_id != null);
    },

    formatClosingBlockLabel(row) {
      const b = row?.block_name;
      if (b != null && String(b).trim() !== "") return String(b).trim();
      return "—";
    },

    formatClosingEntranceLine(row) {
      const e = row?.entrance;
      if (e != null && String(e).trim() !== "") return `Подъезд ${String(e).trim()}`;
      return "";
    },

    formatClosingFloorLabel(row) {
      const x = row?.level_num;
      if (x != null && String(x).trim() !== "") return String(x).trim();
      return "—";
    },

    async exportClosingDocumentExcel(row) {
      if (!this.canExportClosingExcelRow(row)) return;
      const title = this.closingDocumentFileTitle(row);
      try {
        const { downloadClosingDocumentFormExcel } = await import("./services");
        await downloadClosingDocumentFormExcel(this.activeTab, row, title);
      } catch (e) {
        this.showErrorSnackbar(
          e?.response?.data?.message || e?.message || "Не удалось скачать Excel",
        );
      }
    },

    /** Имя для вкладки / подписи: «Бухгалтерский АВР 3-2 - 04.05.2026» (тип + avr_code при наличии + дата из строки). */
    closingDocumentFileTitle(row) {
      if (!row) return "Документ";
      const type = String(row.type_name || "Документ").trim();
      const code = row.avr_code != null && String(row.avr_code).trim() !== ""
        ? String(row.avr_code).trim()
        : "";
      const dateStr = row.doc_date != null && String(row.doc_date).trim() !== ""
        ? String(row.doc_date).trim()
        : "";
      const left = code ? `${type} ${code}` : type;
      return dateStr ? `${left} - ${dateStr}` : left;
    },

    /**
     * Проверка, что тело — PDF; выставляет tab.location на blob URL (как предпросмотр КС-2В).
     */
    _extractClosingErrorText(value) {
      if (value == null) return "";
      if (typeof value === "string") return value.trim();
      if (Array.isArray(value)) {
        return value
          .map((item) => this._extractClosingErrorText(item))
          .filter(Boolean)
          .join("; ")
          .trim();
      }
      if (typeof value === "object") {
        const directKeys = ["error", "message", "detail", "msg", "title"];
        for (const key of directKeys) {
          const text = this._extractClosingErrorText(value[key]);
          if (text) return text;
        }
        return Object.entries(value)
          .filter(([k]) => k !== "status")
          .map(([, v]) => this._extractClosingErrorText(v))
          .filter(Boolean)
          .join("; ")
          .trim();
      }
      return String(value).trim();
    },

    async _buildClosingDownloadErrorMessage(error, fallbackMessage) {
      const status = error?.response?.status;
      const statusText = error?.response?.statusText;
      const statusLine = status ? `${status}${statusText ? `, ${statusText}` : ""}` : "";
      const data = error?.response?.data;
      let detail = "";

      try {
        if (data instanceof Blob) {
          const raw = (await data.text())?.trim();
          if (raw) {
            try {
              detail = this._extractClosingErrorText(JSON.parse(raw));
            } catch (_) {
              detail = this._extractClosingErrorText(raw);
            }
          }
        } else if (data instanceof ArrayBuffer) {
          const raw = new TextDecoder("utf-8").decode(data).trim();
          if (raw) {
            try {
              detail = this._extractClosingErrorText(JSON.parse(raw));
            } catch (_) {
              detail = this._extractClosingErrorText(raw);
            }
          }
        } else if (data != null) {
          detail = this._extractClosingErrorText(data);
        }
      } catch (_) {
        // ignore parse errors, fallback below
      }

      if (!detail) {
        detail = this._extractClosingErrorText(error?.message) || fallbackMessage;
      }

      if (statusLine && detail && !detail.includes(statusLine)) {
        return `${statusLine} — ${detail}`;
      }
      if (statusLine) return statusLine;
      return detail || fallbackMessage;
    },

    async openPdfBlobInBrowserTab(tab, blob, mimeType = "application/pdf", pageTitle = "") {
      const b = blob instanceof Blob ? blob : new Blob([blob], { type: mimeType });
      if (mimeType === "application/pdf" || !mimeType || mimeType === "application/octet-stream") {
        const buf = await b.slice(0, 4).arrayBuffer();
        const sig = String.fromCharCode.apply(null, new Uint8Array(buf));
        if (sig !== "%PDF") {
          const text = await b.text();
          let msg = "Ожидался PDF";
          try {
            const j = JSON.parse(text);
            msg = j.message || j.error || j.detail || msg;
          } catch {
            msg = text.slice(0, 300) || msg;
          }
          throw new Error(msg);
        }
      }
      try {
        if (pageTitle && tab && tab.document) {
          tab.document.title = pageTitle;
        }
      } catch {
        /* ignore */
      }
      const url = URL.createObjectURL(b);
      tab.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 300000);
    },

    /**
     * Подписанный → POST signed/download; иначе GET master_url; иначе aws/doc_url.
     * PDF открывается в новой вкладке (как предпросмотр КС-2В). Имя для человека: см. closingDocumentFileTitle.
     */
    async downloadClosingDocument(row) {
      const qp = this.downloadClosingDocParams(row);
      const docTitle = this.closingDocumentFileTitle(row);

      const rawExternal = String(row.aws_url || row.doc_url || "").trim();
      if (/^https?:\/\//i.test(rawExternal)) {
        window.open(rawExternal, "_blank", "noopener,noreferrer");
        return;
      }

      const masterEarly = String(row.master_url || "").trim();
      if (masterEarly && /^https?:\/\//i.test(masterEarly)) {
        window.open(masterEarly, "_blank", "noopener,noreferrer");
        return;
      }

      const tab = window.open("about:blank", "_blank");
      if (!tab) {
        this.showErrorSnackbar("Разрешите всплывающие окна для этого сайта, чтобы открыть PDF.");
        return;
      }

      try {
        const { default: axiosInstance } = await import("@/services/api");
        const mimeByExt = (ext) => {
          const e = String(ext || "pdf").replace(/^\./, "").toLowerCase();
          const map = {
            pdf: "application/pdf",
            png: "image/png",
            jpg: "image/jpeg",
            jpeg: "image/jpeg",
          };
          return map[e] || "application/octet-stream";
        };

        if (closingDocumentsRowFullySigned(row)) {
          const base = (API_URL || "").replace(/\/$/, "");
          const url = `${base}/project/documents/signed/download/`;
          const res = await axiosInstance(true, null, { showToast: false, throwError: true }).post(
            url,
            { floor_map_document_id: row.floor_map_document_id, ...qp },
            { params: { ...qp }, responseType: "blob" },
          );
          await this.openPdfBlobInBrowserTab(tab, res.data, "application/pdf", docTitle);
          return;
        }

        const master = String(row.master_url || "").trim();
        if (master) {
          const ext = String(row.file_ext || "pdf").replace(/^\./, "");
          const res = await axiosInstance(true, null, { showToast: false, throwError: true }).get(master, {
            params: { ...qp },
            responseType: "blob",
          });
          await this.openPdfBlobInBrowserTab(tab, res.data, mimeByExt(ext), docTitle);
          return;
        }

        const raw = String(row.aws_url || row.doc_url || "").trim();
        if (!raw) {
          try {
            tab.close();
          } catch {
            /* ignore */
          }
          return;
        }

        const path = raw.startsWith("/") ? raw : `/${raw}`;
        const resolved = this.resolveDocumentHref(raw);
        try {
          const res = await axiosInstance(true, null, { showToast: false, throwError: false }).get(path, {
            responseType: "blob",
            _skipErrorToast: true,
          });
          if (!res?.data?.size) {
            try {
              tab.close();
            } catch {
              /* ignore */
            }
            window.open(resolved, "_blank", "noopener,noreferrer");
            return;
          }
          await this.openPdfBlobInBrowserTab(tab, res.data, "application/pdf", docTitle);
        } catch {
          try {
            tab.close();
          } catch {
            /* ignore */
          }
          window.open(resolved, "_blank", "noopener,noreferrer");
        }
      } catch (e) {
        try {
          tab.close();
        } catch {
          /* ignore */
        }
        const message = await this._buildClosingDownloadErrorMessage(e, "Не удалось открыть документ");
        this.showErrorSnackbar(message);
      }
    },

    /**
     * Акт 3КС по связанному документу (id в колонке «№ ЗКС»): подписан — тот же signed/download,
     * иначе — GET /common/3kc_doc/… (рендер через floor_map_3kc_info на бэке).
     */
    async downloadClosingKc3Document(avrRow) {
      const kc3Id = avrRow?.kc3_floor_map_document_id;
      if (kc3Id == null) return;

      const kc3Row = {
        floor_map_document_id: kc3Id,
        assign_signs: Array.isArray(avrRow.kc3_assign_signs) ? avrRow.kc3_assign_signs : [],
        have_signed: avrRow.kc3_have_signed,
        master_url: "",
        aws_url: null,
        doc_url: null,
        file_ext: "pdf",
      };

      const qp = this.downloadClosingDocParams(avrRow);
      const docTitle = `3КС ${kc3Id}`;

      const tab = window.open("about:blank", "_blank");
      if (!tab) {
        this.showErrorSnackbar("Разрешите всплывающие окна для этого сайта, чтобы открыть PDF.");
        return;
      }

      try {
        const { default: axiosInstance } = await import("@/services/api");
        const base = (API_URL || "").replace(/\/$/, "");

        if (closingDocumentsRowFullySigned(kc3Row)) {
          const url = `${base}/project/documents/signed/download/`;
          const res = await axiosInstance(true, null, { showToast: false, throwError: true }).post(
            url,
            { floor_map_document_id: kc3Id, ...qp },
            { params: { ...qp }, responseType: "blob" },
          );
          await this.openPdfBlobInBrowserTab(tab, res.data, "application/pdf", docTitle);
          return;
        }

        const res = await axiosInstance(true, null, { showToast: false, throwError: true }).get(
          `${base}/common/3kc_doc/${kc3Id}/`,
          { params: { ...qp }, responseType: "blob" },
        );
        await this.openPdfBlobInBrowserTab(tab, res.data, "application/pdf", docTitle);
      } catch (e) {
        try {
          tab.close();
        } catch {
          /* ignore */
        }
        const message = await this._buildClosingDownloadErrorMessage(e, "Не удалось открыть акт 3КС");
        this.showErrorSnackbar(message);
      }
    },


    formatSum(val) {
      if (val == null || val === "") return "—";
      const n = parseFloat(val);
      if (!Number.isFinite(n)) return "—";
      return n.toLocaleString("ru-RU", { maximumFractionDigits: 0 }) + " тг";
    },

    scheduleCdTableScrollMeasure() {
      clearTimeout(this.cdTableScrollMeasureTimer);
      this.cdTableScrollMeasureTimer = setTimeout(() => this.measureCdTableScrollWidth(), 80);
    },

    measureCdTableScrollWidth() {
      const run = () => {
        const main = this.$refs.cdScrollMain;
        if (!main || !this.hasActiveFilter) {
          this.cdTableScrollContentWidth = 0;
          this.cdTableScrollBarNeeded = false;
          return;
        }
        const inner = main.querySelector(".cd-table-scroll-inner");
        const table = main.querySelector("table.q-table");
        let w = 0;
        if (inner) {
          w = Math.max(inner.scrollWidth, Math.ceil(inner.getBoundingClientRect().width));
        }
        if (table) {
          const tw = Math.ceil(table.getBoundingClientRect().width);
          w = Math.max(w, tw, table.scrollWidth);
        }
        if (!w) w = main.scrollWidth;
        const cw = main.clientWidth || 0;
        this.cdTableScrollContentWidth = Math.max(w, cw);
        this.cdTableScrollBarNeeded = w > cw + 1;
      };
      requestAnimationFrame(() => {
        this.$nextTick(run);
      });
    },

    onCdTableScrollHostScroll(ev) {
      this.onCdTableScrollSync("main", ev);
    },

    onCdTableScrollSync(_source, ev) {
      if (this.cdTableScrollSyncing) return;
      const src = ev?.target;
      if (!src) return;
      const left = src.scrollLeft;
      this.cdTableScrollSyncing = true;
      const top = this.$refs.cdScrollTop;
      const main = this.$refs.cdScrollMain;
      if (top && top !== src) top.scrollLeft = left;
      if (main && main !== src) main.scrollLeft = left;
      requestAnimationFrame(() => {
        this.cdTableScrollSyncing = false;
      });
    },

    setupCdTableResizeObserver() {
      if (!this.hasActiveFilter) return;
      this.teardownCdTableResizeObserver();
      this.$nextTick(() => {
        const main = this.$refs.cdScrollMain;
        if (!main || typeof ResizeObserver === "undefined") return;
        this.cdTableResizeObserver = new ResizeObserver(() => this.scheduleCdTableScrollMeasure());
        this.cdTableResizeObserver.observe(main);
        const table = main.querySelector("table");
        if (table) this.cdTableResizeObserver.observe(table);
      });
    },

    teardownCdTableResizeObserver() {
      if (this.cdTableResizeObserver) {
        try {
          this.cdTableResizeObserver.disconnect();
        } catch {
          /* ignore */
        }
        this.cdTableResizeObserver = null;
      }
    },

  },

  setup() {
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar, showErrorSnackbar };
  },
};
</script>

<style lang="scss" scoped>
/* Диалог «Выбрать КС-2В для создания АВР»: ширина и скролл тела */
.cd-select-k2sv-card {
  width: min(1280px, 96vw);
  max-width: 96vw;
  max-height: min(92vh, 920px);
  display: flex;
  flex-direction: column;
  min-height: 0;

  .cd-select-k2sv-card__head {
    flex-shrink: 0;
  }

  .cd-select-k2sv-card__body {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
}

.closing-documents {
  padding: 8px 24px 20px;
  margin-top: -4px;
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
  min-height: 0;
  box-sizing: border-box;

  &__page-col {
    flex: 1 1 auto;
    min-height: 0;
    max-height: calc(100vh - 92px);
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow: hidden;
    box-sizing: border-box;
  }

  &__header {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex-shrink: 0;
  }

  &__title-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  &__title {
    font-size: 20px;
    font-weight: 700;
    margin: 0;
    line-height: 1.25;
    color: #1e293b;
  }

  &__tabs {
    border-bottom: 1px solid #e2e8f0;
  }

  &__controls-band {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0 -24px;
    padding: 0 24px 4px;
    flex-shrink: 0;
  }

  &__filters-row {
    position: static;
    display: flex;
    align-items: flex-end;
    gap: 10px;
    flex-wrap: wrap;
    margin: 0;
    padding: 6px 10px 8px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    box-shadow: none;
  }

  &__filters {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    flex: 1;
    min-width: 0;
  }
}

/* Шапка + вкладки + фильтры: вне вертикального скролла таблицы (всегда видны в колонке страницы) */
.cd-sticky-toolbar {
  flex-shrink: 0;
  z-index: 20;
  background: #fff;
  padding-bottom: 2px;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.06);
}

/* Область только для таблицы: вертикальный скролл здесь — тогда thead sticky к этому контейнеру */
.cd-table-body-scroll {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

/* Единый scrollport по X и Y: шапка таблицы position:sticky top:0 работает относительно этого блока */
.closing-documents__table-scroll.cd-table-scroll-host {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  max-width: 100%;
  overflow: auto;
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
}

.cd-table-scroll-inner {
  flex: 0 0 auto;
  min-width: 0;
  box-sizing: border-box;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
}

/* Верхняя видимая полоса (синяя); ширина контента = scrollWidth основной зоны */
.cd-table-scroll-rail {
  box-sizing: border-box;
  overflow-x: auto;
  overflow-y: hidden;
  height: 16px;
  min-height: 14px;
  flex: 0 0 auto;
  border-radius: 8px;
  border: 1px solid #bbdefb;
  background: #e3f2fd;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #1976d2 #e3f2fd;
}

.cd-table-scroll-rail::-webkit-scrollbar {
  height: 10px;
}

.cd-table-scroll-rail::-webkit-scrollbar-track {
  background: #e3f2fd;
  border-radius: 5px;
}

.cd-table-scroll-rail::-webkit-scrollbar-thumb {
  background: #1976d2;
  border-radius: 5px;
}

.cd-table-scroll-rail::-webkit-scrollbar-thumb:hover {
  background: #1565c0;
}

.cd-table-scroll-rail__expander {
  height: 8px;
  min-height: 8px;
}

/*
 * У QTable средняя зона — div.q-table__middle.scroll; класс .scroll из Quasar задаёт overflow,
 * из‑за этого скролл оставался внутри таблицы и верхняя «рейка» не получала правильную ширину.
 */
.cd-table-scroll-inner :deep(.q-table__middle.scroll) {
  overflow-x: visible !important;
  overflow-y: visible !important;
  will-change: auto !important;
}

.cd-table-scroll-inner :deep(.q-table__container) {
  overflow-x: visible !important;
  overflow-y: visible !important;
  max-width: none !important;
}

.cd-table-scroll-inner :deep(table.q-table) {
  width: max-content !important;
  min-width: 100%;
  table-layout: auto !important;
}

.cd-table-scroll-inner .cd-table {
  overflow: visible;
  border-radius: 0;
}

/* Фильтры */
.cd-filter {
  flex: 1;
  min-width: 100px;
  &--sm  { min-width: 140px; max-width: 200px; flex: 1; }
  &--xs  { min-width: 70px;  max-width: 110px; flex: 0 1 90px; }
  &--entrance {
    min-width: 220px;
    max-width: 360px;
    flex: 1 1 280px;
  }
  &--level {
    min-width: 100px;
    max-width: 150px;
    flex: 0 1 130px;
  }
  &--md  { min-width: 168px; max-width: 220px; flex: 0 1 200px; }
}

.cd-create-btn {
  white-space: nowrap;
  flex-shrink: 0;
  height: 40px;
  padding: 0 18px;
}

.cd-preflight-dialog :deep(.q-dialog__inner) {
  padding: 8px;
}

/* Как у глобальной модалки «Создать КС2В»: ширина от контента ~600px, шапка как .modal__title */
.cd-preflight-card {
  width: min(600px, calc(100vw - 24px));
  max-width: 600px;
  border-radius: 8px;
}

.cd-preflight-card__head {
  gap: 10px;
  flex-wrap: nowrap;
  padding: 12px 12px 8px 15px;
}

.cd-preflight-card__head-icon {
  flex-shrink: 0;
}

.cd-preflight-card__title {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-size: 20px;
  line-height: 25px;
  font-weight: 600;
  color: #1e293b;
  word-break: break-word;
}

.cd-preflight-card__body {
  max-height: min(52vh, 400px);
  overflow-y: auto;
  padding: 10px 15px 12px;
}

.cd-preflight-card__hint {
  line-height: 1.4;
}

.cd-preflight-field {
  width: 100%;
}

.cd-preflight-card__validation {
  display: flex;
  align-items: flex-start;
  width: 100%;
  box-sizing: border-box;
  gap: 10px;
  padding: 2px 0 0;
  /* Вровень по горизонтали с текстом внутри dense outlined q-field (~12–14px от рамки) */
  padding-inline-start: 13px;
  margin: 4px 0 0;
  max-width: 100%;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.38;
  color: #1565c0;
}

.cd-preflight-card__validation-icon {
  flex-shrink: 0;
  margin-top: 0.1em;
  /* символ внутри иконки визуально смещён влево относительно текста рядом */
  margin-inline-start: 3px;
}

.cd-preflight-card__validation-text {
  min-width: 0;
}

.cd-preflight-card__actions {
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 15px 14px;
}

/* Подсказка */
.cd-empty-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 32px 24px 40px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  color: #94a3b8;
  font-size: 14px;
  text-align: center;
}

.cd-empty-hint--fill {
  flex: 1 1 auto;
  min-height: 0;
}

/* Счётчики */
.cd-stats {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.cd-stat {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  min-width: 140px;
  flex: 1;
  box-shadow: 0 1px 3px rgba(0,0,0,.04);

  &__body {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  &__value {
    font-size: 24px;
    font-weight: 700;
    line-height: 1;
    color: #1e293b;
  }

  &__label {
    font-size: 11px;
    color: #94a3b8;
    line-height: 1.3;
  }
}

/* Таблица: шапка липнет к верху .cd-table-scroll-host (один scrollport с телом таблицы) */
.cd-table {
  border-radius: 0;
  overflow: visible;

  :deep(thead th) {
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    background: #f8fafc;
    position: sticky;
    top: 0;
    z-index: 12;
    box-shadow: 0 1px 0 #e2e8f0;
  }

  :deep(tbody td) {
    font-size: 13px;
    vertical-align: middle;
    background: #fff;
  }
}

.cd-table :deep(th.cd-col-loc),
.cd-table :deep(td.cd-col-loc) {
  max-width: 168px;
  min-width: 72px;
  white-space: normal;
  word-break: break-word;
  vertical-align: middle;
}

/* КС-2В «Статус» + строка согласования прораба — многострочный текст, ячейка растёт по высоте */
.cd-table :deep(th.cd-col-status--k2sv),
.cd-table :deep(td.cd-col-status--k2sv) {
  max-width: 280px;
  min-width: 160px;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  vertical-align: top;
  padding-top: 10px;
  padding-bottom: 10px;
}

.cd-loc-block__main {
  line-height: 1.35;
}

.cd-loc-block__sub {
  line-height: 1.25;
  margin-top: 2px;
}

.cd-td-downloads {
  min-width: 88px;
}

.cd-k2sv-followup {
  min-width: 132px;

  &__btn {
    min-width: 118px;
  }

  &__state {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 5px 10px;
    border-radius: 8px;
    max-width: 168px;
    margin: 0 auto;
    cursor: default;
    line-height: 1.25;
    text-align: left;
  }

  &__state--wait {
    background: #f8fafc;
    border: 1px dashed #94a3b8;
  }

  &__state--done {
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
  }

  &__icon {
    flex-shrink: 0;
  }
}

/* Колонка «1С»: фиксированная ширина, перенос вниз; горизонтально не заезжаем на «Автор» */
.cd-table :deep(th.cd-col-1c),
.cd-table :deep(td.cd-col-1c) {
  width: 200px;
  max-width: 200px;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: visible;
  vertical-align: middle;
  white-space: normal;
}

.cd-td-1c {
  width: 200px;
  max-width: 200px;
  overflow-x: hidden;
  overflow-y: visible;
  box-sizing: border-box;
  vertical-align: middle;
  white-space: normal;
}

.cd-status-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.cd-status-stack {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  min-width: 0;
  width: 100%;

  :deep(.q-chip) {
    align-self: flex-start;
    max-width: 100%;
    white-space: normal;
    height: auto;
    min-height: 28px;
  }
}

.cd-k2sv-prorab-badge {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: nowrap;
  gap: 5px;
  align-self: flex-start;
  box-sizing: border-box;
  padding: 4px 10px;
  border-radius: 8px;
  border: 1px solid #6ee7b7;
  background: linear-gradient(180deg, #ecfdf5 0%, #d1fae5 100%);
  color: #065f46;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.25;
  max-width: 100%;

  &__ico {
    flex-shrink: 0;
  }

  &__label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 180px;
  }
}

.cd-status-block--kc3 {
  align-items: center;
  width: 100%;
}

.cd-1c {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: visible;
  box-sizing: border-box;

  > * {
    min-width: 0;
    max-width: 100%;
  }

  &__codes {
    width: 100%;
    max-width: 100%;
    text-align: center;
    line-height: 1.35;
  }

  &__err {
    margin-top: 2px;
    display: flex;
    justify-content: center;
    max-width: 100%;
  }

  &__err-trigger {
    min-height: auto;
    min-width: auto;
  }

  &__line {
    font-size: 11px;
  }

  &__line--avr {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    max-width: 100%;
  }

  &__guid-row {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    margin-top: 2px;
  }

  &__guid-prefix {
    flex-shrink: 0;
    font-size: 10px;
    line-height: 1.2;
  }

  &__guid-mono {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    text-align: center;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-all;
    font-size: 10px;
    line-height: 1.25;
    font-family: ui-monospace, "Cascadia Mono", Consolas, monospace;
  }
}

/* Колонка «Контрагент»: перенос длинного названия на следующие строки */
.cd-table :deep(th.cd-col-contractor),
.cd-table :deep(td.cd-col-contractor) {
  max-width: 280px;
  vertical-align: top;
  white-space: normal;
}

.cd-td-contractor {
  max-width: 280px;
  vertical-align: top;
  white-space: normal;
}

/* Ячейка «Проект» */
.cd-proj {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 260px;

  &__top {
    min-width: 0;
    max-width: 100%;
  }

  &__pid {
    flex-shrink: 0;
  }

  &__name {
    font-weight: 500;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &--with-pid {
      flex: 1 1 auto;
      min-width: 0;
    }
  }

  &__jk {
    font-size: 11px;
    color: #64748b;
  }

  &__type {
    display: inline-block;
    font-size: 10px;
    font-weight: 500;
    color: #1565c0;
    background: #e3f2fd;
    border-radius: 4px;
    padding: 1px 5px;
    width: fit-content;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 240px;
  }

  &__loc {
    font-size: 11px;
    color: #64748b;
    line-height: 1.35;
    margin-top: 1px;
  }
}

/* Контрагент: имя переносится внутри столбца (не одна строка с ellipsis) */
.cd-proj--contractor {
  max-width: 100%;
  min-width: 0;

  .cd-proj__name {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    line-height: 1.35;
    overflow-wrap: anywhere;
  }

  .cd-proj__type {
    white-space: normal;
    max-width: 100%;
    overflow-wrap: anywhere;
  }
}

.cd-table :deep(th.cd-col-kc3-id),
.cd-table :deep(td.cd-col-kc3-id) {
  width: 88px;
  max-width: 88px;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: visible;
  vertical-align: middle;
  white-space: normal;
}

.cd-table :deep(th.cd-col-kc3-status),
.cd-table :deep(td.cd-col-kc3-status) {
  min-width: 100px;
  max-width: 160px;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: visible;
  vertical-align: middle;
  white-space: normal;
}

.cd-table :deep(th.cd-col-kc3-dl),
.cd-table :deep(td.cd-col-kc3-dl) {
  width: 96px;
  max-width: 96px;
  min-width: 72px;
  box-sizing: border-box;
  vertical-align: middle;
}

/* Блок 3КС: № ЗКС, статус, скачивание — светло-голубой фон */
.cd-table :deep(th.cd-col-3kc-accent),
.cd-table :deep(td.cd-col-3kc-accent) {
  background: #e3f2fd !important;
}

.cd-td-kc3-download {
  width: 96px;
  max-width: 96px;
  vertical-align: middle;
}

.cd-td-kc3-id {
  width: 88px;
  max-width: 88px;
  overflow-x: hidden;
  overflow-y: visible;
  box-sizing: border-box;
  vertical-align: middle;
  white-space: normal;
}

.cd-td-kc3-status {
  min-width: 100px;
  max-width: 160px;
  box-sizing: border-box;
  vertical-align: middle;
}

.cd-kc3-id-wrap {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}

.cd-kc3-id__mono {
  display: block !important;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  line-height: 1.25;
}

.cd-kc3-status__text {
  line-height: 1.35;
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* Код документа */
.cd-code {
  font-family: monospace;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  background: #f1f5f9;
  border-radius: 4px;
  padding: 2px 6px;
  display: inline-block;
}

.cd-code--pid {
  font-size: 11px;
  font-weight: 500;
}

.cd-stat__hint-icon {
  margin-left: 4px;
  vertical-align: -2px;
}

/* Сумма */
.cd-sum {
  font-weight: 500;
  color: #1e293b;
  font-variant-numeric: tabular-nums;
}

.cd-m19-cmt-dialog :deep(.q-dialog__inner) {
  padding: 12px;
}

.cd-m19-cmt-card {
  width: min(1100px, 96vw);
  max-width: 96vw;
  max-height: min(88vh, 900px);
  display: flex;
  flex-direction: column;
}

.cd-m19-cmt-card > .q-card__section:not(.cd-m19-cmt-card__body),
.cd-m19-cmt-card > .q-separator {
  flex-shrink: 0;
}

.cd-m19-cmt-card__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}
</style>

<style lang="scss">
/* Тултип монтируется в body — без scoped */
.cd-status-signers-tt {
  max-width: 360px;
  background: #263238 !important;
  color: #eceff1 !important;

  .cd-status-signers-tt__row {
    line-height: 1.45;
    padding: 2px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    font-size: 12px;
    white-space: normal;
  }

  .cd-status-signers-tt__row:last-of-type {
    border-bottom: none;
  }
}

/* q-menu монтируется в body — без scoped */
.cd-err-menu-panel {
  max-width: min(480px, 92vw);
}

.cd-err-menu-panel .cd-err-menu-panel__text {
  padding: 12px 14px;
  user-select: text;
  cursor: text;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.45;
}
</style>
